import '/modules/common/css/layer/layer.css';
import '/modules/common/css/layer/layer.responsive.css';
import * as $ from 'jquery';
import {UIElement} from "../ui/ui";
import {Image as ImageLayer, Tile} from "ol/layer";
import {TILE_IMAGE_SIZE, TILE_IMAGE_SIZE_MOBILE, WGS84_CODE} from "../config/config";
import {Cluster, ImageWMS, TileWMS} from "ol/source";
import {Slider} from "../ui/Slider";
import {Dropdown, DropdownOption} from "../ui/Dropdown";
import {
    dateFromStr,
    dateFromYearMonthDay,
    formatDate,
    formatDateDailyYearless,
    formatDateTenDailyYearless,
    TimeCalculator
} from "../time/TimeCalculator";
import {
    DeviceVisibilityEnum,
    LayerGroupTypeEnum,
    LayerTypeEnum,
    PeriodicityEnum,
    ProtocolEnum,
    TimeScaleEnum
} from "../enum/enum";
import {Legend} from "./Legend";
import VectorSource from "ol/source/Vector";
import {GeoJSON} from "ol/format";
import {bbox as bboxStrategy} from 'ol/loadingstrategy';
import VectorLayer from "ol/layer/Vector";
import {ChartStyler, Styler} from "./styles";
import {CoordinatesPanel} from "../ui/CoordinatesPanel";
import {FeatureTimeseriesFetcher, TimeseriesFetcher} from "../timeseries/TimeseriesFetcher";
import {isNumber, isString, nnTrim} from "../util";
import {formatWgs84, reducePrecisionWgs84, transformExtent} from "../geolib";
import {getCenter, intersects} from "ol/extent";
import {initNamespace} from "../main/MainTumboApplication";
import {Base} from "../Base";
import {transform} from "ol/proj";
import {Fill, Style, Text} from "ol/style";
import {isGraphicLowContrastOnWhite} from "../colors";
import {Point} from "ol/geom";
import TileLayer from "ol/layer/Tile";
import {DatePicker} from "../timeseries/DatePicker";
import {FeatureIndicatorChart, IndicatorChart} from "../timeseries/IndicatorChart";
import {LayerConfig} from "../config/MainConfig";

function _toggleExclusiveLayerVisibility(layer, input) {
    const visible = input.val() === layer.getCode();
    layer.logStat(visible ? "SHOW" : "HIDE");
    layer.setVisible(visible);
}

/**
 * A layer tree UI element
 * @extends UIElement
 */
class LayerTree extends UIElement {

    /**
     *
     * @param translator {Translator} A translator
     * @param isMobileDevice {boolean} true if this is a mobile device. false otherwise
     * @param slimPanel {SlimPanel} A slim panel to display the layers options
     * @param chartModal {Modal} A modal to show the timeseries chart in
     * @param infoModal {Modal} A modal to show indicator information in
     * @param layerTreeConfigs {Array<LayerTreeElementConfig>} The configurations for the tree
     * @param tileGridGetter {function} A function that given a service config, returns a map's TileGrid
     * @param projection {Projection} Map's current projection
     * @param runtimeConfig {MapRuntimeConfig} The map runtime config
     * @param onChangeRuntimeConfig {function} A callback to be called when runtimeConfig is changed
     * @param onChangeVisibleLayers {function} A callback to be called when visible layers change
     * @param fit {function} A function that takes an extent (wgs84) and fits the map there
     */
    constructor(translator, isMobileDevice, slimPanel, chartModal, infoModal, layerTreeConfigs, tileGridGetter, projection, runtimeConfig, onChangeRuntimeConfig, onChangeVisibleLayers, fit = undefined) {
        super('layer_tree_ui_element', translator, isMobileDevice, runtimeConfig, onChangeRuntimeConfig);
        this.slimPanel = slimPanel;
        this.chartModal = chartModal;
        this.infoModal = infoModal;
        this.layerTreeConfigs = layerTreeConfigs;
        this.tileGridGetter = tileGridGetter;
        this.projection = projection;
        this.onChangeVisibleLayers = onChangeVisibleLayers;
        this.fit = fit;

        this.layerTree = [];
        this.oLLayers = [];
        this.oLVectorOverlays = [];
        for (let lt in this.layerTreeConfigs) {
            let layerTreeConfig = this.layerTreeConfigs[lt];
            const deviceVisibility = layerTreeConfig.getDeviceVisibility();
            if (deviceVisibility === DeviceVisibilityEnum.BOTH || (!this.isMobileDevice && deviceVisibility === DeviceVisibilityEnum.DESKTOP) || (this.isMobileDevice && deviceVisibility === DeviceVisibilityEnum.MOBILE)) {
                if (layerTreeConfig.getType() === LayerGroupTypeEnum.CLASSIFIER) {
                    let layerClassifier = new LayerTreeClassifier(translator, isMobileDevice, slimPanel, chartModal, infoModal, layerTreeConfig, tileGridGetter, projection, runtimeConfig, onChangeRuntimeConfig, false, 'root', onChangeVisibleLayers);
                    this.layerTree.push(layerClassifier);
                    const renderOrdinal = layerClassifier.getRenderOrdinal();
                    this.oLLayers[renderOrdinal !== undefined && renderOrdinal !== null ? renderOrdinal : lt] = layerClassifier.getOLLayers();
                    this.oLVectorOverlays = this.oLVectorOverlays.concat(layerClassifier.getOLVectorOverlays());
                } else if (layerTreeConfig.getType() === LayerGroupTypeEnum.LAYER) {
                    let layer = new Layer(translator, isMobileDevice, slimPanel, chartModal, infoModal, layerTreeConfig, tileGridGetter, projection, runtimeConfig, onChangeRuntimeConfig, false, 'root', onChangeVisibleLayers)
                        .add2LayersByTimeTag();
                    this.layerTree.push(layer);
                    const renderOrdinal = layer.getRenderOrdinal();
                    this.oLLayers[renderOrdinal !== undefined && renderOrdinal !== null ? renderOrdinal : lt] = layer.getOLLayers();
                    this.oLVectorOverlays = this.oLVectorOverlays.concat(layer.getOLVectorOverlays());
                }
            }
        }

    }

    /**
     * Get the OpenLayers layers
     * @return {Array<Layer>}
     */
    getOLLayers() {
        let res = [];
        const processMap = (map) => {
            Object.keys(map).sort().forEach(ordinal => {
                if (Array.isArray(map[ordinal]))
                    res = res.concat(map[ordinal]);
                else processMap(map[ordinal]);
            })
        }
        processMap(this.oLLayers);
        return res;
    }

    /**
     * Get all the layer overlays, if any
     * @return {Array<Overlay>}
     */
    getOverlays() {
        return this.getLayers()
            .map(l => l.getOverlays())
            .flat();
    }

    /**
     * Get the OpenLayers vector overlays
     * @return {Array<VectorLayer>}
     */
    getOLVectorOverlays() {
        return this.oLVectorOverlays;
    }

    getUIElement() {
        if (!this.uiElement) {
            this.uiElement = $('<div class="layer-tree-body"></div>');
            let ul = $('<ul role="tree"></ul>');
            this.uiElement.append(ul);
            for (let lt in this.layerTree) {
                let li = $('<li role="treeitem"></li>');
                li.append(this.layerTree[lt].getUIElement());
                ul.append(li);
            }


            if (this.fit)
                this.getLayers().forEach(l => {
                    if (l.getExtentWgs84()) {
                        $('.layer-container.layer-code-' + l.getCodeHtmlClass() + ' .layer-options button.layer-zoom2extent-button', this.uiElement)
                            .click(() => {
                                this.fit(l.getExtentWgs84());
                                this.logStat('ZOOM_TO_LAYER_EXTENT {' + l.getCode() + '}');
                            });
                    }
                });
        }
        return this.uiElement;
    }

    /**
     * Set the map's current projection
     * @param projection {Projection}
     */
    setProjection(projection) {
        this.projection = projection;
        for (let lt in this.layerTree) {
            this.layerTree[lt].setProjection(projection);
        }
    }

    /**
     * Get the list of visible layers
     * @return {Array<Layer>}
     */
    getVisibleLayers() {
        let result = [];
        for (let lt in this.layerTree) {
            result = result.concat(this.layerTree[lt].getVisibleLayers());
        }
        return result;
    }

    /**
     * Get all the layers
     * @return {Array<Layer>}
     */
    getLayers() {
        let result = [];
        for (let lt in this.layerTree) {
            result = result.concat(this.layerTree[lt].getLayers());
        }
        return result;
    }

    /**
     * To be called by TumboMap when there is navigation on the map
     * It should not be called more than 1 time every 500ms
     * @param zoomLevel {number} The new zoom level (integer)
     * @param centerWgs84 {Array<number>} The new center coordinates (WGS84)
     * @param extentWgs84 {Array<number>|undefined} The new map extent (WGS84)
     */
    onNavigation(zoomLevel, centerWgs84, extentWgs84) {
        for (let lt in this.layerTree) {
            this.layerTree[lt].onNavigation(zoomLevel, centerWgs84, extentWgs84);
        }
    }
}

/**
 * A layer list
 * @extends UIElement
 */
class LayerList extends UIElement {

    /**
     * @param id {string} A uinique id of this layer list
     * @param translator {Translator} A translator
     * @param isMobileDevice {boolean} true if this is a mobile device. false otherwise
     * @param slimPanel {SlimPanel} A slim panel to display the layers options
     * @param infoModal {Modal} A modal to show indicator information in
     * @param layersConfig {Array<LayerListElementConfig>} The configurations for the layers (or layer groups) in the list
     * @param tileGridGetter {function} A function that given a service config, returns a map's TileGrid
     * @param projection {Projection} Map's current projection
     * @param runtimeConfig {MapRuntimeConfig} The map runtime config
     * @param onChangeRuntimeConfig {function} A callback to be called when runtimeConfig is changed
     * @param onChangeVisibleLayers {function} A callback to be called when visible layers change
     */
    constructor(id, translator, isMobileDevice, slimPanel, infoModal, layersConfig, tileGridGetter, projection, runtimeConfig, onChangeRuntimeConfig, onChangeVisibleLayers) {
        super(id, translator, isMobileDevice, runtimeConfig, onChangeRuntimeConfig);
        this.slimPanel = slimPanel;
        this.infoModal = infoModal;
        this.layersConfig = layersConfig;
        this.tileGridGetter = tileGridGetter;
        this.projection = projection;
        this.onChangeVisibleLayers = onChangeVisibleLayers;

        this.oLLayers = [];
        this.layers = [];
        this.listElements = [];
    }

    /**
     * Get the OpenLayers layers
     * @return {Array<Layer>}
     */
    getOLLayers() {
        return this.oLLayers;
    }

    /**
     * Get all the layer overlays, if any
     * @return {Array<Overlay>}
     */
    getOverlays() {
        return this.layers
            .map(l => l.getOverlays())
            .flat();
    }

    /**
     * Set the map's current projection
     * @param projection {Projection}
     */
    setProjection(projection) {
        this.projection = projection;
        for (let l in this.layers) {
            this.layers[l].setProjection(projection);
        }
    }

    /**
     * To be called by TumboMap when there is navigation on the map
     * It should not be called more than 1 time every 500ms
     * @param zoomLevel {number} The new zoom level (integer)
     * @param centerWgs84 {Array<number>} The new center coordinates (WGS84)
     * @param extentWgs84 {Array<number>} The new map extent (WGS84)
     */
    onNavigation(zoomLevel, centerWgs84, extentWgs84) {
        for (let l in this.layers) {
            this.layers[l].onNavigation(zoomLevel, centerWgs84, extentWgs84);
        }
    }

    /**
     * Get the list of visible layers
     * @return {Array<Layer>}
     */
    getVisibleLayers() {
        return this.layers.filter(l => l.isVisible());
    }

    /**
     * Get the list of all layers
     * @return {Array<Layer>}
     */
    getLayers() {
        return this.layers;
    }
}

/**
 * A list of base layers an UI element
 * @extends LayerList
 * @hideconstructor
 */
class BaseLayerList extends LayerList {
    constructor(translator, isMobileDevice, slimPanel, infoModal, layersConfig, tileGridGetter, projection, runtimeConfig, onChangeRuntimeConfig, onChangeVisibleLayers, setMapBackgroundColor) {
        super('base_layer_list', translator, isMobileDevice, slimPanel, infoModal, layersConfig, tileGridGetter, projection, runtimeConfig, onChangeRuntimeConfig, onChangeVisibleLayers);
        this.layersConfig.forEach(listElementConfig => {
            const deviceVisibility = listElementConfig.getDeviceVisibility();
            if (deviceVisibility === DeviceVisibilityEnum.BOTH || (!this.isMobileDevice && deviceVisibility === DeviceVisibilityEnum.DESKTOP) || (this.isMobileDevice && deviceVisibility === DeviceVisibilityEnum.MOBILE)) {
                if (listElementConfig.getType() === LayerGroupTypeEnum.CLASSIFIER) {
                    const children = listElementConfig.getChildren().map(childConfig => {
                        return new BaseLayer(translator, isMobileDevice, slimPanel, infoModal, childConfig, tileGridGetter, projection, runtimeConfig, onChangeRuntimeConfig, onChangeVisibleLayers, setMapBackgroundColor);
                    });
                    children.forEach(child => {
                        this.layers.push(child);
                        this.oLLayers = this.oLLayers.concat(child.getOLLayers())
                    });
                    const layerListClassifier = new LayerListClassifier(translator, isMobileDevice, listElementConfig, children, runtimeConfig, onChangeRuntimeConfig);
                    this.listElements.push(layerListClassifier);
                } else {
                    const baseLayer = new BaseLayer(translator, isMobileDevice, slimPanel, infoModal, listElementConfig, tileGridGetter, projection, runtimeConfig, onChangeRuntimeConfig, onChangeVisibleLayers, setMapBackgroundColor);
                    this.layers.push(baseLayer);
                    this.listElements.push(baseLayer);
                    this.oLLayers = this.oLLayers.concat(baseLayer.getOLLayers());
                }
            }
        });
    }

    getUIElement() {
        if (!this.uiElement) {
            this.uiElement = $('<div role="list" class="base-layer-list-body"></div>');
            this.listElements.forEach(elem => this.uiElement.append(elem.getUIElement()));

            this.layers.forEach(baseLayer => {
                const input = $('.checkbox-container>input[type=radio][name=base_layer]', this.uiElement);
                input.change(function () {
                    _toggleExclusiveLayerVisibility(baseLayer, $(this));
                });
                baseLayer.visibility(baseLayer.isVisible());
                if (!baseLayer.isEnabled())
                    baseLayer.disable();
            });
        }
        return this.uiElement;
    }

    /**
     * Enable this base layer list
     */
    enable() {
        this.layers.forEach(l => {
            l.enable()
        });
    }

    /**
     * Disable this base layer list
     */
    disable() {
        this.layers.forEach(l => {
            l.disable()
        });
    }
}

/**
 * A list of context layers an UI element
 * @extends LayerList
 * @hideconstructor
 */
class ContextLayerList extends LayerList {
    constructor(translator, isMobileDevice, slimPanel, infoModal, layersConfig, tileGridGetter, projection, runtimeConfig, onChangeRuntimeConfig, onChangeVisibleLayers) {
        super('context_layer_list', translator, isMobileDevice, slimPanel, infoModal, layersConfig, tileGridGetter, projection, runtimeConfig, onChangeRuntimeConfig, onChangeVisibleLayers);

        this.layersConfig.forEach(listElementConfig => {
            const deviceVisibility = listElementConfig.getDeviceVisibility();
            if (deviceVisibility === DeviceVisibilityEnum.BOTH || (!this.isMobileDevice && deviceVisibility === DeviceVisibilityEnum.DESKTOP) || (this.isMobileDevice && deviceVisibility === DeviceVisibilityEnum.MOBILE)) {
                if (listElementConfig.getType() === LayerGroupTypeEnum.CLASSIFIER) {
                    const children = listElementConfig.getChildren().map(childConfig => {
                        return new ContextLayer(translator, isMobileDevice, slimPanel, infoModal, childConfig, tileGridGetter, projection, runtimeConfig, onChangeRuntimeConfig, onChangeVisibleLayers);
                    });
                    children.forEach(child => {
                        this.layers.push(child);
                        this.oLLayers = this.oLLayers.concat(child.getOLLayers())
                    });
                    const layerListClassifier = new LayerListClassifier(translator, isMobileDevice, listElementConfig, children, runtimeConfig, onChangeRuntimeConfig);
                    this.listElements.push(layerListClassifier);
                } else {
                    const contextLayer = new ContextLayer(translator, isMobileDevice, slimPanel, infoModal, listElementConfig, tileGridGetter, projection, runtimeConfig, onChangeRuntimeConfig, onChangeVisibleLayers);
                    this.layers.push(contextLayer);
                    this.listElements.push(contextLayer);
                    this.oLLayers = this.oLLayers.concat(contextLayer.getOLLayers());
                }
            }
        });
    }

    getUIElement() {
        if (!this.uiElement) {
            this.uiElement = $('<div role="list" class="context-layer-list-body"></div>');
            for (const cl in this.listElements) {
                this.uiElement.append(this.listElements[cl].getUIElement());
            }
        }
        return this.uiElement;
    }
}

/**
 * A layer classifier (tree node)
 * @extends UIElement
 */
class LayerTreeClassifier extends UIElement {

    /**
     *
     * @param translator {Translator} A translator
     * @param isMobileDevice {boolean} true if this is a mobile device. false otherwise
     * @param slimPanel {SlimPanel} A slim panel to display the layers options
     * @param chartModal {Modal} A modal to show the timeseries chart in
     * @param infoModal {Modal} A modal to show indicator information in
     * @param classifierConfig {Object} The classifier config
     * @param tileGridGetter {function} A function that given a service config, returns a map's TileGrid
     * @param projection {Projection} Map's current projection
     * @param runtimeConfig {MapRuntimeConfig} The map runtime config
     * @param onChangeRuntimeConfig {function} A callback to be called when runtimeConfig is changed
     * @param exclusive {boolean} Is this node mutually exclusive with its siblings?
     * @param parentName {string} The name of the parent of this classifier
     * @param onChangeVisibleLayers {function} A callback to be called when visible layers change
     */
    constructor(translator, isMobileDevice, slimPanel, chartModal, infoModal, classifierConfig, tileGridGetter, projection, runtimeConfig, onChangeRuntimeConfig, exclusive, parentName, onChangeVisibleLayers) {
        super(classifierConfig.getCode(), translator, isMobileDevice, runtimeConfig, onChangeRuntimeConfig);
        this.slimPanel = slimPanel;
        this.chartModal = chartModal;
        this.infoModal = infoModal;
        this.classifierConfig = classifierConfig;
        this.exclusive = exclusive;
        this.parentName = parentName;
        this.oLLayers = {};
        this.oLVectorOverlays = [];
        this.children = [];
        this.locationVisible = true;
        this.onChangeVisibleLayers = onChangeVisibleLayers;
        let childrenConfig = this.classifierConfig.getChildren();
        for (let c in childrenConfig) {
            let childConfig = childrenConfig[c];
            const deviceVisibility = childConfig.getDeviceVisibility();
            if (deviceVisibility === DeviceVisibilityEnum.BOTH || (!this.isMobileDevice && deviceVisibility === DeviceVisibilityEnum.DESKTOP) || (this.isMobileDevice && deviceVisibility === DeviceVisibilityEnum.MOBILE)) {
                if (childConfig.getType() === LayerGroupTypeEnum.CLASSIFIER) {
                    let layerClassifier = new LayerTreeClassifier(translator, isMobileDevice, slimPanel, chartModal, infoModal, childConfig, tileGridGetter, projection, runtimeConfig, onChangeRuntimeConfig, this.classifierConfig.isMutuallyExclusive(), this.getCode(), this.onChangeVisibleLayers);
                    const renderOrdinal = layerClassifier.getRenderOrdinal();
                    this.oLLayers[renderOrdinal !== undefined && renderOrdinal !== null ? renderOrdinal : c] = layerClassifier.getOLLayers();
                    this.oLVectorOverlays = this.oLVectorOverlays.concat(layerClassifier.getOLVectorOverlays());
                    this.children.push(layerClassifier);
                } else if (childConfig.getType() === LayerGroupTypeEnum.LAYER) {
                    let layer = new Layer(translator, isMobileDevice, slimPanel, chartModal, infoModal, childConfig, tileGridGetter, projection, runtimeConfig, onChangeRuntimeConfig, this.classifierConfig.isMutuallyExclusive(), this.getCode(), this.onChangeVisibleLayers)
                        .add2LayersByTimeTag();
                    const renderOrdinal = layer.getRenderOrdinal();
                    this.oLLayers[renderOrdinal !== undefined && renderOrdinal !== null ? renderOrdinal : c] = layer.getOLLayers();
                    this.oLVectorOverlays = this.oLVectorOverlays.concat(layer.getOLVectorOverlays());
                    this.children.push(layer);
                }
            }
        }
        this.visibility(this.isVisible());
    }

    getCode() {
        return this.classifierConfig.getCode();
    }

    getRenderOrdinal() {
        return this.classifierConfig.getRenderOrdinal();
    }

    /**
     * Get the OpenLayers layers
     * @return {Array<Layer>}
     */
    getOLLayers() {
        let res = [];
        const processMap = (map) => {
            Object.keys(map).sort().forEach(ordinal => {
                if (Array.isArray(map[ordinal]))
                    res = res.concat(map[ordinal]);
                else processMap(map[ordinal]);
            })
        }
        processMap(this.oLLayers);
        return res;
    }

    /**
     * Get the OpenLayers vector overlays
     * @return {Array<VectorLayer>}
     */
    getOLVectorOverlays() {
        return this.oLVectorOverlays;
    }

    getUIElement() {
        if (!this.uiElement) {
            const isExpanded = this.isExpanded();
            this.uiElement = $(
                '<div class="layer-classifier-container layer-classifier-code-' + this.getCodeHtmlClass() + ' ' + (isExpanded ? 'expanded' : '') + '">'
                + '  <div class="layer-classifier-header tree-header">'
                + '    <button tabindex="0" class="caret ' + (isExpanded ? 'caret-down' : '') + '" title="' + this.translator.translate('layer.tree.expander') + '"></button>'
                + '    <div class="layer-classifier-checkbox-container checkbox-container">'
                + '        <input tabindex="0" id="classifier_' + this.getCodeHtmlClass() + '" value="' + (this.getCode()) + '" name="' + (this.parentName) + '" type="' + (this.exclusive ? 'radio' : 'checkbox') + '" ' + (this.isVisible() ? 'checked="checked"' : '') + ' />'
                + '    </div>'
                + '    <div class="layer-classifier-name"><label for="classifier_' + this.getCodeHtmlClass() + '">' + this.classifierConfig.getName() + '</label></div>'
                + '  </div>'
                + '  <div role="group" aria-expanded="' + (!!isExpanded) + '" class="layer-classifier-body" ' + (isExpanded ? '' : 'hidden') + ' style="' + (!isExpanded ? 'display:none' : '') + '"></div>'
                + '</div>'
            );
            const self = this;
            $('.layer-classifier-checkbox-container>input[type=checkbox]', this.uiElement).change(function () {
                const visible = $(this).is(':checked');
                self.logStat(visible ? "SHOW" : "HIDE");
                self.setVisible(visible);
            });

            $('.layer-classifier-header button.caret', this.uiElement).click(() => {
                const expand = !this.isExpanded();
                this.logStat(expand ? "EXPAND" : "COLLAPSE");
                this.setExpanded(expand);
            });

            const ul = $('<ul></ul>');
            this.children.forEach(child => {
                ul.append($('<li role="treeitem"></li>').append(child.getUIElement()));
            });
            $('.layer-classifier-body', this.uiElement).append(ul);

            if (this.classifierConfig.isMutuallyExclusive()) {
                this.children.forEach(child => {
                    const input = $('.checkbox-container>input[type=radio][name=' + (this.getCode()) + ']', this.uiElement);
                    input.change(function () {
                        _toggleExclusiveLayerVisibility(child, $(this));
                    });
                    child.visibility(child.isVisible());
                });
            }
        }
        return this.uiElement;
    }

    /**
     * Is this classifier visible? (means: are its child layers visible in the map?)
     * @return {boolean}
     */
    isVisible() {
        return this.classifierConfig.isVisible();
    }

    /**
     * Is this classifier expanded?
     * @return {boolean}
     */
    isExpanded() {
        return this.classifierConfig.isExpanded();
    }

    /**
     * Expand/collapse classifier
     * @param isExpanded {boolean} if true, expands
     */
    setExpanded(isExpanded) {
        const container = $('.layer-classifier-code-' + this.getCodeHtmlClass());
        const caret = $('.layer-classifier-code-' + this.getCodeHtmlClass() + '>.layer-classifier-header>button.caret');
        const body = $('.layer-classifier-code-' + this.getCodeHtmlClass() + '>.layer-classifier-body');

        if (isExpanded) {
            caret.addClass('caret-down');
            container.addClass('expanded');
            body.slideDown(600).removeAttr('hidden').attr('aria-expanded', 'true');
        } else {
            caret.removeClass('caret-down');
            container.removeClass('expanded')
            body.slideUp(400).attr('hidden', '').attr('aria-expanded', 'false');
        }

        this.runtimeConfig.setClassifierExpanded(this.getCode(), isExpanded);
        this.onChangeRuntimeConfig();
    }

    /**
     * set classifier visible (means: are set child layers visible in the map, unless those are themselves not visible)
     * @param isVisible {boolean} if true, sets visible
     */
    setVisible(isVisible) {
        this.runtimeConfig.setClassifierVisibility(this.getCode(), isVisible);
        this.onChangeRuntimeConfig();
        this.visibility(isVisible);
    }

    visibility(isVisible) {
        isVisible = isVisible && this.isVisible();
        if (!isVisible || !this.locationVisible) {
            this.disable();
            $($('.checkbox-container>input', this.getUIElement())[0]).removeAttr('disabled');
        } else if (isVisible && this.locationVisible) {
            this.enable();
        }
    }

    /**
     * Set the map's current projection
     * @param projection {Projection}
     */
    setProjection(projection) {
        this.projection = projection;
        for (let c in this.children) {
            this.children[c].setProjection(projection);
        }
    }

    /**
     * Disable this classifier
     */
    disable() {
        this.getUIElement().addClass('disabled');
        $($('.checkbox-container>input', this.getUIElement())[0]).attr('disabled', 'disabled');
        this.children.forEach(child => {
            child.disable();
        });
    }

    /**
     * Enable this classifier
     */
    enable() {
        $($('.checkbox-container>input', this.getUIElement())[0]).removeAttr('disabled');
        if (this.isVisible()) {
            this.getUIElement().removeClass('disabled');
            this.children.forEach(child => {
                child.enable();
            });
        }
    }

    /**
     * Get the list of visible layers
     * @return {Array<Layer>}
     */
    getVisibleLayers() {
        if (!this.isVisible()) {
            return [];
        }
        let result = [];
        for (let c in this.children) {
            result = result.concat(this.children[c].getVisibleLayers());
        }
        return result;
    }

    /**
     * Get all the layers
     * @return {Array<Layer>}
     */
    getLayers() {
        let result = [];
        for (let lt in this.children) {
            result = result.concat(this.children[lt].getLayers());
        }
        return result;
    }

    onNavigation(zoomLevel, centerWgs84, extentWgs84) {
        this.children.forEach(child => {
            child.onNavigation(zoomLevel, centerWgs84, extentWgs84);
        });
    }
}

/**
 * A layer classifier (list group)
 * @extends UIElement
 */
class LayerListClassifier extends UIElement {

    /**
     *
     * @param translator {Translator} A translator
     * @param isMobileDevice {boolean} true if this is a mobile device. false otherwise
     * @param classifierConfig {Object} The classifier config
     * @param children {Array<BaseLayer>} The children of this classifier
     * @param runtimeConfig {MapRuntimeConfig} The map runtime config
     * @param onChangeRuntimeConfig {function} A callback to be called when runtimeConfig is changed
     */
    constructor(translator, isMobileDevice, classifierConfig, children, runtimeConfig, onChangeRuntimeConfig) {
        super(classifierConfig.getCode(), translator, isMobileDevice, runtimeConfig, onChangeRuntimeConfig);
        this.classifierConfig = classifierConfig;
        this.children = children;
    }

    getCode() {
        return this.classifierConfig.getCode();
    }

    getUIElement() {
        if (!this.uiElement) {
            if (this.children.filter(c => c.isVisible()).length > 0) {
                this.setExpanded(true);
            }

            const isExpanded = this.isExpanded();
            this.uiElement = $(
                '<div class="layer-list-classifier-container layer-list-classifier-code-' + this.getCodeHtmlClass() + ' ' + (isExpanded ? 'expanded' : '') + '">'
                + '  <div class="layer-list-classifier-header tree-header">'
                + '    <button class="caret ' + (isExpanded ? 'caret-down' : '') + '" title="' + this.translator.translate('layer.tree.expander') + '"></button>'
                + '    <div class="layer-list-classifier-name"><label>' + this.classifierConfig.getName() + '</label></div>'
                + '  </div>'
                + '  <div role="group" aria-expanded="' + (!!isExpanded) + '" class="layer-list-classifier-body" ' + (isExpanded ? '' : 'hidden') + ' style="' + (!isExpanded ? 'display:none' : '') + '"></div>'
                + '</div>'
            );
            $('.layer-list-classifier-header button.caret', this.uiElement).click(() => {
                const expand = !this.isExpanded();
                this.logStat(expand ? "EXPAND" : "COLLAPSE");
                this.setExpanded(expand);
            });

            const ul = $('<ul></ul>');
            this.children.forEach(child => {
                ul.append($('<li role="listitem"></li>').append(child.getUIElement()));
            });
            $('.layer-list-classifier-body', this.uiElement).append(ul);
        }
        return this.uiElement;
    }

    /**
     * Is this classifier expanded?
     * @return {boolean}
     */
    isExpanded() {
        return this.classifierConfig.isExpanded();
    }

    /**
     * Expand/collapse classifier
     * @param isExpanded {boolean} if true, expands
     */
    setExpanded(isExpanded) {
        const container = $('.layer-list-classifier-code-' + this.getCodeHtmlClass());
        const caret = $('.layer-list-classifier-code-' + this.getCodeHtmlClass() + '>.layer-list-classifier-header>button.caret');
        const body = $('.layer-list-classifier-code-' + this.getCodeHtmlClass() + '>.layer-list-classifier-body');

        if (isExpanded) {
            caret.addClass('caret-down');
            container.addClass('expanded');
            body.slideDown(600).removeAttr('hidden').attr('aria-expanded', 'true');
        } else {
            caret.removeClass('caret-down');
            container.removeClass('expanded')
            body.slideUp(400).attr('hidden', '').attr('aria-expanded', 'false');
        }

        this.runtimeConfig.setClassifierExpanded(this.getCode(), isExpanded);
        this.onChangeRuntimeConfig();
    }
}

/**
 * A layer service. Has a direct correspondence with an OpenLayers layer
 */
class Service extends Base {

    /**
     *
     * @param serviceConfig {LayerServiceConfig} The service configuration
     * @param isMobileDevice {boolean} true if this is a mobile device. false otherwise
     * @param productConfig {ProductConfig} The product configuration
     * @param projection {Projection} Map's current projection
     * @param tileGridGetter {function} A function that given a service config, returns a map's TileGrid
     * @param opacity {number} The initial opacity
     * @param visible {boolean} The initial visibility
     * @param runtimeConfig {MapRuntimeConfig}
     */
    constructor(serviceConfig, isMobileDevice, productConfig, projection, tileGridGetter, opacity, visible, runtimeConfig) {
        super();
        this.isMobileDevice = isMobileDevice
        this.serviceConfig = serviceConfig
        this.productConfig = productConfig
        this.projection = projection
        this.tileGridGetter = tileGridGetter;
        this.opacity = opacity;
        this.visible = visible;
        this.coordinatesPanel = undefined;
        this.runtimeConfig = runtimeConfig;
    }

    /**
     * An ID token to use for logging
     * @return {string}
     */
    getLogId() {
        return nnTrim(this.getClassName(), this.getClass()) + " [" + this.getCode() + "]";
    }

    /**
     * Get a value by property key
     * @param prop {string} A property key
     * @return {*}
     */
    getSessionProp(prop) {
        return this.runtimeConfig.getUIElementSessionProp(this.getCode(), prop);
    }

    /**
     * Set a property value that lasts for this session (ends on close tab)
     * @param prop {string} A property key
     * @param value {*} any value
     */
    setSessionProp(prop, value) {
        return this.runtimeConfig.setUIElementSessionProp(this.getCode(), prop, value);
    }

    /**
     * Remove a property
     * @param prop {string} A property key
     */
    removeSessionProp(prop) {
        return this.runtimeConfig.removeUIElementSessionProp(this.getCode(), prop);
    }

    getCode() {
        return this.serviceConfig.getCode();
    }

    /**
     * Show a popup for a feature of this service
     * Only applicable if this is a vector service
     * @param feature {Feature} An OpenLayers feature belonging to this service
     * @param pixelX {number} The X pixel coordinate of the click (integer)
     * @param pixelY {number} The Y pixel coordinate of the click (integer)
     */
    showFeatureInfo(feature, coordinateWgs84, pixelX, pixelY) {
    }

    _showFeatureInfoOnCoordinate(coordinateWgs84, feature, pixelX, pixelY) {
    }

    /**
     * Hide the popup for a feature of this service
     * Only applicable if this is a vector service
     */
    hideFeatureInfo() {
    }

    /**
     * Get the layer overlay, if this is a clickable vector service
     * @return {Overlay}
     */
    getOverlay() {
    }

    /**
     * Get the OpenLayers source
     * @return {Source}
     * @private
     */
    getOLLayerSource() {
        const tileSize = this.serviceConfig.getTileSize()
            ? this.serviceConfig.getTileSize()
            : (this.isMobileDevice ? TILE_IMAGE_SIZE_MOBILE : TILE_IMAGE_SIZE);
        const params = {
            HEIGHT: tileSize,
            WIDTH: tileSize,
            TILED: true,
            FORMAT: this._getImageFormat(),
            VERSION: '1.1.1',
            SRS: this.projection.getCode(),
            CRS: this.projection.getCode(),
            TRANSPARENT: this._isTransparent()
        };
        return new TileWMS({
            url: this.serviceConfig.getUrl(),
            params: params,
            serverType: 'mapserver',
            crossOrigin: 'Anonymous',
            transition: 0,
            interpolate: false,
            tileGrid: this.tileGridGetter(this.serviceConfig),
            attributions: ['<div>' + this.productConfig.getName() + '</div>']
        });
    }

    _isTransparent() {
        return true;
    }

    _getImageFormat() {
        return 'image/png';
    }

    /**
     *
     * @return {Layer} Get the OpenLayers layer
     */
    getOlLayer() {
        if (!this.olLayer) {
            this.olLayer = new Tile({
                source: this.getOLLayerSource(),
                extent: this.getExtent(),
                opacity: this.opacity,
                visible: this.visible
            });
            this.olLayer.set('code', this.serviceConfig.getCode(), true);
        }
        return this.olLayer;
    }

    /**
     * The OpenLayers vector overlays
     * @return {Array<VectorLayer>} OpenLayers vector layers
     */
    getOLVectorOverlays() {
    }

    /**
     * Set the OpenLayers layer opacity
     * @param opacity {number} [0 .. 1]
     */
    setOpacity(opacity) {
        this.opacity = opacity;
        this.getOlLayer().setOpacity(opacity);
    }

    /**
     * Set the OpenLayers layer visibility
     * @param visible {boolean}
     */
    setVisibility(visible) {
        this.visible = visible;
        this.getOlLayer().setVisible(visible);
        const olVectorOverlays = this.getOLVectorOverlays();
        if (olVectorOverlays) {
            olVectorOverlays.forEach(vol => vol.setVisible(visible));
        }
        if (this.coordinatesPanel) {
            if (!visible && this.coordinatesPanel.isVisible()) {
                this.hideFeatureInfo();
            }
        }
    }

    _update() {
        const olLayer = this.getOlLayer();
        olLayer.setSource(this.getOLLayerSource());
        this.hideFeatureInfo();
    }

    /**
     * Set the current map projection
     * @param projection {Projection}
     */
    setProjection(projection) {
        this.projection = projection;
        this.getOlLayer().setExtent(this.getExtent());
        this._update();
    }

    /**
     * Get the layer extent in map current projection
     * @return {Array<number>}
     */
    getExtent() {
        return transformExtent(this.serviceConfig.getExtentWgs84(), WGS84_CODE, this.projection.getCode());
    }

    getExtentWgs84() {
        return this.serviceConfig.getExtentWgs84();
    }

    onAdd2Map() {
    }
}

/**
 * A service for a layer from the layer tree
 * @extends Service
 */
class BaseLayerService extends Service {

    _getImageFormat() {
        return 'image/jpeg';
    }

    _isTransparent() {
        return false;
    }
}

/**
 * A service for a layer from the layer tree
 * @extends Service
 */
class LayerService extends Service {

    /**
     * @param translator {Translator} A translator
     * @param isMobileDevice {boolean} true if this is a mobile device. false otherwise
     * @param runtimeConfig {MapRuntimeConfig} The map runtime config
     * @param onChangeRuntimeConfig {function} A callback to be called when runtimeConfig is changed
     * @param clusterSizePixels {number|undefined} The cluster size in pixels. if undefined or <10 no cluster will be added
     * @param serviceConfig {LayerServiceConfig} The service configuration
     * @param productConfig {ProductConfig} The product configuration
     * @param projection {Projection} Map's current projection
     * @param tileGridGetter {function} A function that given a service config, returns a map's TileGrid
     * @param opacity {number} The initial opacity
     * @param visible {boolean} The initial visibility
     * @param timeCode {string} The initial timeCode
     * @param timeScale {TimeScaleEnum} The initial timeScale
     * @param timeCodeCalculator {TimeCalculator} The time calculator
     * @param style {Style} The style to render this layer in, if VECTOR
     * @param chartable {boolean} If true, adds a chart button to the vector layer feature popup
     * @param showChart {function} If chartable, this function should show a chart, given the feature properties, the clicked WGS84 coordinates and a name for the feature
     */
    constructor(translator, isMobileDevice, runtimeConfig, onChangeRuntimeConfig, clusterSizePixels, serviceConfig, productConfig, projection, tileGridGetter, opacity, visible, timeCode, timeScale, timeCodeCalculator, style, chartable = false, showChart = undefined) {
        super(serviceConfig, isMobileDevice, productConfig, projection, tileGridGetter, opacity, visible, runtimeConfig);
        this.translator = translator;
        this.runtimeConfig = runtimeConfig;
        this.onChangeRuntimeConfig = onChangeRuntimeConfig;
        this.clusterSizePixels = clusterSizePixels && clusterSizePixels >= 10 ? clusterSizePixels : undefined;
        this.timeCode = timeCode;
        this.timeScale = timeScale;
        this.timeCodeCalculator = timeCodeCalculator;
        this.style = style;
        this.oLVectorOverlays = [];
        this.chartable = chartable;
        this.showChart = showChart;
        if (this.serviceConfig.isClickable()) {
            this.coordinatesPanel = new CoordinatesPanel(
                translator,
                isMobileDevice,
                'click_coordinates_panel_' + serviceConfig.getCode(),
                'vector-click-coordinates-panel',
                runtimeConfig,
                onChangeRuntimeConfig
            );
            this.coordinatesPanel.onHide(() => {
                this.removeSessionProp("featureInfoProps");
                this.removeSessionProp("featureInfoCoords");
            });
        }
    }

    getOlLayer() {
        if (this.serviceConfig.getLayerType() === LayerTypeEnum.RASTER) {
            if (this.serviceConfig.getProtocol() === ProtocolEnum.WMS)
                return super.getOlLayer();
            else if (this.serviceConfig.getProtocol() === ProtocolEnum.WMS_SINGLE_IMAGE) {
                if (!this.olLayer) {
                    this.olLayer = new ImageLayer({
                        extent: this.getExtent(),
                        opacity: this.opacity,
                        visible: this.visible,
                        source: this.getOLLayerSource()
                    });
                    this.olLayer.set('code', this.serviceConfig.getCode(), true);
                }
                return this.olLayer;
            }
        }
        if (this.serviceConfig.getLayerType() === LayerTypeEnum.VECTOR) {
            if (!this.olLayer) {
                let vector;
                if (this.clusterSizePixels) {

                    const olLayerSource = this.getOLLayerSource();
                    const clusterSource = new Cluster({
                        distance: this.clusterSizePixels,
                        minDistance: this.clusterSizePixels,
                        source: olLayerSource,
                    });
                    clusterSource.set('code', olLayerSource.get('code'));

                    const styleCache = {};
                    const olStyle = this.getStyler().getOLStyle();
                    vector = new VectorLayer({
                        source: clusterSource,
                        style: feature => {
                            const features = feature.get('features');
                            const size = features.length;
                            if (size > 1) {
                                const length = size.toString().length;
                                if (!styleCache[length]) {
                                    const clusterImageStyle = new Styler(this.style).getClusterImageStyle();
                                    clusterImageStyle.setRadius(10 + 5 * length);
                                    styleCache[length] = new Style({
                                        image: clusterImageStyle,
                                        text: new Text({
                                            text: size.toString(),
                                            font: '12px sans-serif',
                                            fill: new Fill({
                                                color: isGraphicLowContrastOnWhite(clusterImageStyle.getFill().getColor()) ? 'black' : 'white',
                                            })
                                        })
                                    });
                                }
                                return styleCache[length];
                            } else return olStyle(features[0]);
                        }
                    });
                    vector.set('clustered', true);
                } else {
                    vector = new VectorLayer({
                        source: this.getOLLayerSource(),
                        style: this.getStyler().getOLStyle(),
                        extent: this.getExtent(),
                        opacity: this.opacity,
                        visible: this.visible
                    });
                }

                vector.set('code', this.serviceConfig.getCode(), true);
                vector.set('hasOverlay', this.getOLVectorOverlays().length > 0, true);
                this.olLayer = vector;
            }
            return this.olLayer;
        }
    }

    getStyler() {
        return new Styler(this.style);
    }

    getChartStyler() {
        return new ChartStyler(this.style);
    }

    getOverlay() {
        if (this.coordinatesPanel)
            return this.coordinatesPanel.getOverlay();
    }

    showFeatureInfo(feature, coordinateWgs84, pixelX, pixelY) {
        if (this.serviceConfig.isClickable()) {
            if (!coordinateWgs84) {
                const geometry = feature.getGeometry();
                const centroid = getCenter(geometry.getExtent());
                const currentProjectionCode = this.projection.getCode();
                coordinateWgs84 = currentProjectionCode === WGS84_CODE
                    ? centroid
                    : transform(centroid, currentProjectionCode, WGS84_CODE);
            }
            let properties = feature.getProperties();
            if (properties.features && properties.features.length === 1) //deal with clustered layers
                properties = properties.features[0].getProperties();

            const geom = properties.features && properties.features.length === 1
                ? properties.features[0].getGeometry()
                : feature.getGeometry();
            const showCoords = geom instanceof Point;

            this._showFeatureInfoOnCoordinate(properties, coordinateWgs84, showCoords, pixelX, pixelY);
        }
    }

    _showFeatureInfoOnCoordinate(properties, coordinateWgs84, showCoords, pixelX, pixelY) {
        if (this.serviceConfig.isClickable()) {
            this.setSessionProp("featureInfoProps", properties);
            this.setSessionProp("featureInfoCoords", coordinateWgs84);
            this.setSessionProp("featureInfoShowCoords", showCoords);

            const getPopupContents = () => {
                const element = $('<div></div>');

                if (showCoords) {
                    element.append($('<div class="coords"><label>' + this.translator.translate('layer.coordinatesWgs84Label') + ':</label> ' + formatWgs84(coordinateWgs84) + '</div>'))
                }

                let chartButton = undefined;
                if (this.chartable && this.showChart) {
                    const idPropName = this.getIDPropName(properties);
                    const id = properties[idPropName];
                    if (id) {
                        const chartButtonContainer = $('<div class="chart"><label>' + this.translator.translate('layer.indicator.vectorChartLabel') + '</label><div class="chart-button-container"></div></div>');
                        element.append(chartButtonContainer);
                        chartButton = $('<button></button>').addClass('chart-button');
                        $('.chart-button-container', chartButtonContainer).append(chartButton);
                    }
                }

                let featureName = undefined;

                const featureParser = FEATURE_PARSER_BY_SERVICE_CODE[this.serviceConfig.getCode()];
                if (featureParser) {
                    const timeParameters = this.getTimeParameters();
                    const featurePropertiesMap = featureParser.getFormattedProperties(properties, this.getChartStyler(), timeParameters);
                    const wmsUrl = nnTrim(featureParser.getWmsUrl(properties, timeParameters));
                    const name = nnTrim(featureParser.getName(properties));
                    if (name)
                        featureName = name;
                    if (wmsUrl) {
                        const tileSize = this.serviceConfig.getTileSize()
                            ? this.serviceConfig.getTileSize()
                            : (this.isMobileDevice ? TILE_IMAGE_SIZE_MOBILE : TILE_IMAGE_SIZE);
                        this.temporaryWMSLayer = new TileLayer({
                            source: new TileWMS({
                                url: wmsUrl,
                                params: {
                                    'TILED': true,
                                    'HEIGHT': tileSize,
                                    'WIDTH': tileSize,
                                    'SRS': this.projection.getCode()
                                },
                                serverType: 'mapserver',
                                tileGrid: this.tileGridGetter ? this.tileGridGetter(this.serviceConfig) : undefined
                            })
                        });
                        const map = this.coordinatesPanel.getOverlay().getMap();
                        const index = map.getLayers().getArray().indexOf(this.olLayer);
                        map.getLayers().insertAt(index - 1, this.temporaryWMSLayer);
                    }
                    for (let label in featurePropertiesMap) {
                        element.append($('<div class="property"><label>' + label + '</label>' + nnTrim(featurePropertiesMap[label], '--') + '</div>'))
                    }
                }

                if (chartButton)
                    chartButton.click(() => this.showChart(properties, coordinateWgs84, featureName));

                return {
                    contents: element,
                    title: this.productConfig.getName(dateFromStr(this.timeCode)) + (featureName ? ' - ' + featureName : '')
                };
            };

            const popupContents = getPopupContents();
            this.coordinatesPanel.setTitle(popupContents.title.crop(30));
            this.coordinatesPanel.setContent(popupContents.contents);
            this.coordinatesPanel.showOnGeoCoords(coordinateWgs84);
            this.coordinatesPanel.onHide(() => {
                const layers = this.coordinatesPanel.getOverlay().getMap().getLayers();
                layers.removeAt(layers.getArray().indexOf(this.temporaryWMSLayer));
            });
        }
    }

    hideFeatureInfo() {
        if (this.coordinatesPanel) {
            this.coordinatesPanel.hideNoAnimation();
        }
    }

    getOLLayerSource() {
        let initialParams = this.getTimeParameters();

        if (this.serviceConfig.getLayerType() === LayerTypeEnum.RASTER) {
            this.olSource = this._getRasterOLSource(initialParams);
        } else if (this.serviceConfig.getLayerType() === LayerTypeEnum.VECTOR) {
            this.olSource = this._getVectorOLSource(initialParams);
        }
        if (this.olSource) {
            this.olSource.set('code', this.serviceConfig.getCode(), true);
        }
        return this.olSource;
    }

    getTimeParameters() {
        const periodicity = this.productConfig.getPeriodicity();
        const timeScale = this.getTimeScaleStrForRequest();
        let timeParams = {};
        if (URL_PARAMS_GENERATOR_BY_SERVICE_CODE[this.getCode()]) {
            try {
                timeParams = URL_PARAMS_GENERATOR_BY_SERVICE_CODE[this.getCode()](this.timeCode, timeScale);
            } catch (e) {
                this.logError("Error generating url params for " + this.serviceConfig.getCode(), e)
            }
        } else {
            const timeSelector = this.serviceConfig.getTimeSelector();
            if (timeSelector) {
                const timeSelectorSplit = timeSelector.split(',');
                if (timeSelectorSplit.length > 1) {
                    const timeCodeSplit = this.timeCode.split("-");
                    for (let i = 0; i < Math.min(timeSelectorSplit.length, timeCodeSplit.length); i++) {
                        const paramName = timeSelectorSplit[i].trim();
                        if (paramName.length > 0)
                            timeParams[paramName] = timeCodeSplit[i];
                    }
                } else timeParams[timeSelector] = this.timeCode;
            } else {
                const timeCodeSplit = this.timeCode ? this.timeCode.split('-') : undefined;
                if (periodicity && timeCodeSplit) {
                    if (!this.timeCodeCalculator.isYearless()) {
                        timeParams.SELECTED_YEAR = timeCodeSplit[0];
                    }
                    switch (periodicity) {
                        case PeriodicityEnum.YEARLY:
                            break;
                        case PeriodicityEnum.MONTHLY:
                            timeParams.SELECTED_MONTH = timeCodeSplit[1];
                            break;
                        case PeriodicityEnum.MONTHLY_FORECAST:
                            timeParams.SELECTED_MONTH = timeCodeSplit[1];
                            break;
                        case PeriodicityEnum.MONTHLY_UPDATED_TEN_DAILY:
                        case PeriodicityEnum.TEN_DAILY_YEARLESS_30DAY:
                        case PeriodicityEnum.TEN_DAILY_YEARLESS:
                        case PeriodicityEnum.TEN_DAILY:
                            timeParams.SELECTED_MONTH = timeCodeSplit[1];
                            timeParams.SELECTED_TENDAYS = timeCodeSplit[2];
                            break;
                        case PeriodicityEnum.WEEKLY:
                            timeParams.SELECTED_WEEK = this.timeCodeCalculator.getWeekFromDate(dateFromStr(this.timeCode)).pad(2);
                            break;
                        case PeriodicityEnum.ISO_WEEKLY:
                            timeParams.SELECTED_WEEK = this.timeCodeCalculator.getISOWeekFromDate(dateFromStr(this.timeCode)).pad(2);
                            break;
                        case PeriodicityEnum.CUSTOM:
                            break;
                        case PeriodicityEnum.DAILY:
                        case PeriodicityEnum.DAILY_YEARLESS:
                        case PeriodicityEnum.LATEST:
                            timeParams.SELECTED_MONTH = timeCodeSplit[1];
                            timeParams.SELECTED_DAY = timeCodeSplit[2];
                        default:
                            break;
                    }
                }
            }
            if (timeScale) {
                timeParams.SELECTED_TIMESCALE = timeScale;
            }
        }
        return timeParams;
    }

    getTimeScaleStrForRequest() {
        return this.serviceConfig.getTimeScales().length > 0
            ? (this.timeScale && this.serviceConfig.getTimeScales().indexOf(this.timeScale) >= 0
                ? this.timeScale.getCode()
                : this.serviceConfig.getTimeScales()[0].getCode())
            : undefined;
    }

    getOLVectorOverlays() {
        return this.oLVectorOverlays;
    }

    getIDPropName(properties) {
        const possibleIDPropNames = ["id", "ID", "fid", "FID"];
        const propNames = Object.keys(properties);
        let idProp;
        for (const name of possibleIDPropNames)
            if (propNames.indexOf(name) >= 0) {
                idProp = name;
                break;
            }
        return idProp;
    }

    _getVectorOLSource(initialParams) {
        const url = this.serviceConfig.getUrl();
        if (this.serviceConfig.getProtocol() === ProtocolEnum.WFS) {
            const geoJSONFormat = new GeoJSON();
            let vectorSource; //ugly stuff: the loader function for this source refers to the source itself
            vectorSource = new VectorSource({
                projection: this.projection.getCode(),
                loader: (extent, resolution, projection, success, failure) => {
                    const currentProjCode = this.projection.getCode();
                    const originalCRS = this.serviceConfig.getOriginalCRS();
                    const needsTransform = originalCRS && originalCRS !== currentProjCode;
                    if (needsTransform)
                        extent = transformExtent(extent, currentProjCode, originalCRS);

                    let finalUrl = url + '&request=GetFeature&outputFormat=application/json' +
                        '&srsName=' + this.projection.getCode() +
                        '&service=WFS' +
                        '&bbox=' + extent.join(',') + ',' + (needsTransform ? originalCRS : currentProjCode);
                    for (let t in initialParams) {
                        finalUrl += ('&' + t + '=' + initialParams[t]);
                    }
                    $.ajax(finalUrl, {
                        method: 'GET',
                        error: e => {
                            this.logError('vector source fetching error: ' + finalUrl, e);
                            failure();
                        },
                        success: data => {
                            try {
                                if (isString((data)))
                                    data = JSON.parse(data)
                                if (data.features) {
                                    const features = geoJSONFormat.readFeatures(data)
                                        .sort((f1, f2) => {
                                            const idPropName1 = this.getIDPropName(f1.getProperties());
                                            const idPropName2 = this.getIDPropName(f2.getProperties());
                                            if (idPropName1 && !idPropName2)
                                                return 1;
                                            if (idPropName2 && !idPropName1)
                                                return -1;
                                            if (!idPropName1 && !idPropName2)
                                                return 0;

                                            const id1 = f1.getProperties()[idPropName1];
                                            const id2 = f2.getProperties()[idPropName2];
                                            if (id1 !== undefined && id1 !== null && (id2 === undefined || id2 === null))
                                                return 1;
                                            if (id2 !== undefined && id2 !== null && (id1 === undefined || id1 === null))
                                                return -1;
                                            if ((id1 === undefined || id1 === null) && (id2 === undefined || id2 === null))
                                                return 0;

                                            if (isNumber(id1) && isNumber(id2))
                                                return id1 > id2 ? 1 : id2 > id1 ? -1 : 0;

                                            return id1.toString().localeCompare(id2.toString());
                                        });
                                    const ids = [];
                                    features.forEach(f => { //avoid repeated features when zooming
                                        if (f.getId() === null || f.getId() === undefined) {
                                            const idPropName = this.getIDPropName(f.getProperties());
                                            let id = idPropName
                                                ? f.getProperties()[idPropName]
                                                : ids.length.toString();
                                            if (ids.indexOf(id) >= 0)
                                                id += "_" + ids.length;
                                            ids.push(id);
                                            f.setId(id);
                                        } else {
                                            ids.push(f.getId());
                                        }
                                    });
                                    if (needsTransform)
                                        features.forEach(f => f.getGeometry().transform(originalCRS, currentProjCode));
                                    vectorSource.addFeatures(features);
                                    success(features);
                                }
                            } catch (e) {
                                this.logError('vector source parsing error', e);
                                failure();
                            }
                        }
                    });
                },
                strategy: bboxStrategy,
                attributions: ['<div>' + this.productConfig.getName(dateFromStr(this.timeCode)) + '</div>']
            });

            if (this.serviceConfig.isClickable()) {
                const highlightStyle = this.getStyler().getOLHighlightStyle();
                this.oLVectorOverlays = [new VectorLayer({
                    source: new VectorSource(),
                    style: highlightStyle,
                })];
                this.oLVectorOverlays[0].set('serviceCode', this.serviceConfig.getCode(), true);
            }

            return vectorSource;
        }
    }

    _getRasterOLSource(initialParams) {
        const params = {
            VERSION: '1.1.1',
            SRS: this.projection.getCode(),
            FORMAT: 'image/png'
        };
        Object.keys(initialParams).forEach(iP => params[iP] = initialParams[iP]);

        if (this.serviceConfig.getProtocol() === ProtocolEnum.WMS) {
            const tileSize = this.serviceConfig.getTileSize()
                ? this.serviceConfig.getTileSize()
                : (this.isMobileDevice ? TILE_IMAGE_SIZE_MOBILE : TILE_IMAGE_SIZE);
            params.HEIGHT = tileSize;
            params.WIDTH = tileSize;
            params.TILED = true;
            const tileWMS = new TileWMS({
                url: this.serviceConfig.getUrl().format([Number(this.getTimeScaleStrForRequest()) + ""]),
                params: params,
                serverType: 'mapserver',
                transition: 0,
                crossOrigin: 'Anonymous',
                interpolate: false,
                tileGrid: this.tileGridGetter ? this.tileGridGetter(this.serviceConfig) : undefined,
                attributions: ['<div>' + this.productConfig.getName(dateFromStr(this.timeCode)) + '</div>']
            });
            return tileWMS;
        }
        if (this.serviceConfig.getProtocol() === ProtocolEnum.WMS_SINGLE_IMAGE) {
            return new ImageWMS({
                url: this.serviceConfig.getUrl().format([Number(this.getTimeScaleStrForRequest()) + ""]),
                params: params,
                ratio: 1,
                crossOrigin: 'Anonymous',
                projection: this.projection.getCode(),
                attributions: ['<div>' + this.productConfig.getName(dateFromStr(this.timeCode)) + '</div>']
            });
        }
    }

    /**
     * Set this layer current time code
     * @param timeCode {string}
     */
    setTimeCode(timeCode) {
        this.timeCode = timeCode;
        this._update();
    }

    /**
     * Set this layer current time scale
     * @param timeScale {TimeScaleEnum}
     */
    setTimeScale(timeScale) {
        this.timeScale = timeScale;
        this._update();
    }

    setProjection(projection) {
        super.setProjection(projection);
        if (this.coordinatesPanel) {
            this.coordinatesPanel.setProjection(projection);
        }
    }

    onAdd2Map() {
        super.onAdd2Map();
        if (this.getSessionProp("featureInfoProps") && this.getSessionProp("featureInfoCoords")) {
            this._showFeatureInfoOnCoordinate(this.getSessionProp("featureInfoProps"), this.getSessionProp("featureInfoCoords"), this.getSessionProp("featureInfoShowCoords"), undefined, undefined);
        }
    }
}

/**
 * A layer
 * @abstract
 * @extends UIElement
 */
class AbstractLayer extends UIElement {

    /**
     *
     * @param translator {Translator} A translator
     * @param isMobileDevice {boolean} true if this is a mobile device. false otherwise
     * @param slimPanel {SlimPanel} A slim panel to display the layers options
     * @param infoModal {Modal} A modal to show indicator information in
     * @param layerConfig {Object} The layer config
     * @param tileGridGetter {function} A function that given a service config, returns a map's TileGrid
     * @param projection {Projection} Map's current projection
     * @param runtimeConfig {RuntimeConfig} The map runtime config
     * @param onChangeRuntimeConfig {function} A callback to be called when runtimeConfig is changed
     * @param onChangeVisibleLayers {function} A callback to be called when this layer changes visibility
     */
    constructor(translator, isMobileDevice, slimPanel, infoModal, layerConfig, tileGridGetter, projection, runtimeConfig, onChangeRuntimeConfig, onChangeVisibleLayers) {
        super(layerConfig.getCode(), translator, isMobileDevice, runtimeConfig, onChangeRuntimeConfig);
        this.layerConfig = layerConfig;
        this.tileGridGetter = tileGridGetter;
        this.projection = projection;
        this.slimPanel = slimPanel;
        this.infoModal = infoModal;
        this.locationVisible = true;
        this.opacityValue = 1;
        this.enabled = true;
        this.onChangeVisibleLayers = onChangeVisibleLayers;

        let styles = this.layerConfig.getStyles();
        if (this.layerConfig.getDrivenServices) {
            const drivenServices = this.layerConfig.getDrivenServices();
            for (let ds in drivenServices) {
                if (drivenServices[ds].getStyle())
                    styles.push(drivenServices[ds].getStyle());
            }
        }
        if (styles.length > 0) {
            this.legend = new Legend(this.isMobileDevice, this.translator, styles);
            this.chartLegend = new Legend(this.isMobileDevice, this.translator, [styles[0]]);
            this.chartStyler = new ChartStyler(styles[0]);
        }
        if (this.layerConfig.getAccumulatedStyle && this.layerConfig.getAccumulatedStyle())
            this.accumulatedChartStyle = new ChartStyler(this.layerConfig.getAccumulatedStyle());
    }

    getClassName() {
        return "AbstractLayer";
    }

    /**
     *
     * @return {Array<Styler>}
     */
    getStylers() {
        return this.services.map(s => s.getStyler()).flat();
    }

    _initServices() {
    }

    /**
     * This layer's legend
     * @return {Legend}
     */
    getLegend() {
        return this.legend;
    }

    /**
     * This layer's legend
     * @return {Legend}
     */
    getChartLegend() {
        return this.chartLegend;
    }

    /**
     *
     * @return {ChartStyler}
     */
    getChartStyler() {
        return this.chartStyler;
    }

    /**
     *
     * @return {ChartStyler}
     */
    getAccumulatedChartStyler() {
        return this.accumulatedChartStyle;
    }

    /**
     *
     * @return {LayerConfig}
     */
    getConfig() {
        return this.layerConfig;
    }

    /**
     * The name of this layer
     * @return {string}
     */
    getName() {
        return this.getProductConfig().getName();
    }

    /**
     * This layer's product config
     * @return {ProductConfig}
     */
    getProductConfig() {
        return this.layerConfig.getProduct();
    }

    /**
     * This layer's service config
     * @return {LayerServiceConfig}
     */
    getServiceConfig() {
        return this.layerConfig.getService();
    }

    getCode() {
        return this.layerConfig.getCode();
    }

    /**
     * Override to implement whatever should happen when the tool is added to the map
     */
    onAdd2Map() {
        this.services.forEach(s => s.onAdd2Map());
    }

    /**
     * Get the layer overlays, for this layer's clickable vector services, if any
     * @return {Array<Overlay>}
     */
    getOverlays() {
        return this.services.map(s => s.getOverlay()).filter(o => o !== undefined);
    }

    /**
     * Does this layer have the service for the given code?
     * @param code {string} the service code
     * @return {boolean}
     */
    hasService(code) {
        return this.layerConfig.getService().getCode() === code;
    }

    /**
     * Show a popup for a feature of this layer
     * @param serviceCode {string} The layer service code
     * @param feature {Feature} An OpenLayers feature belonging to this layer
     * @param coordinateWgs84 {Array<number>} Coordinates in WGS84
     * @param pixelX {number} The X pixel coordinate of the click (integer)
     * @param pixelY {number} The Y pixel coordinate of the click (integer)
     */
    showFeatureInfo(serviceCode, feature, coordinateWgs84, pixelX, pixelY) {
    }

    /**
     * Hide the popup for a feature of this layer
     * @param serviceCode {string} The layer service code
     */
    hideFeatureInfo(serviceCode) {
    }

    /**
     * Hide the popups for a feature of this layer
     */
    hideFeatureInfos(serviceCode) {
    }

    /**
     * The OpenLayers layers
     * @return {Array<Layer>} OpenLayers layers
     */
    getOLLayers() {
        const res = [];
        for (let i = this.services.length - 1; i >= 0; i--)
            res.push(this.services[i].getOlLayer());
        return res;
    }

    /**
     * The OpenLayers vector overlays
     * @return {Array<VectorLayer>} OpenLayers vector layers
     */
    getOLVectorOverlays() {
        let res = [];
        for (let i = this.services.length - 1; i >= 0; i--)
            res = res.concat(this.services[i].getOLVectorOverlays());
        return res;
    }

    /**
     * Set the layer opacity
     * @param opacity {number} [0 .. 1]
     */
    setOpacity(opacity) {
        this.opacityValue = Math.max(0.2, opacity !== undefined ? Number(opacity) : 1);
        this.services.forEach(service => {
            service.setOpacity(this.opacityValue);
        });
        this.visibility();
    }

    /**
     * Get the layer extent in map current projection
     * @return {Array<number>}
     */
    getExtent() {
        return this.services[0].getExtent();
    }

    getExtentWgs84() {
        return this.services[0].getExtentWgs84();
    }

    /**
     * Is this layer visible in the map?
     * @return {boolean}
     */
    isVisible() {
        return this.layerConfig.isVisible();
    }

    /**
     * Is this layer enabled in the map?
     * @return {boolean}
     */
    isEnabled() {
        return this.enabled;
    }

    /**
     * Set this layer visible in the map
     * @param isVisible {boolean}
     */
    setVisible(isVisible) {
    }

    /**
     * Is this layer identifiable?
     * @return {boolean}
     */
    isIdentifiable() {
        return this.layerConfig.isIdentifiable();
    }

    /**
     * Set the map's current projection
     * @param projection {Projection}
     */
    setProjection(projection) {
        this.projection = projection;
        for (let ls in this.services)
            this.services[ls].setProjection(projection);
    }

    /**
     * Disable this layer
     */
    disable() {
        this.enabled = false;
        $('.checkbox-container>input', this.getUIElement()).attr('disabled', 'disabled');
        this.visibility();
    }

    /**
     * Enable this layer
     */
    enable() {
        this.enabled = true;
        $('.checkbox-container>input', this.getUIElement()).removeAttr('disabled');
        this.visibility();
    }

    visibility(visible) {
        const vis = visible !== undefined ? visible : this._getVisibility();
        this.services.forEach(service => {
            service.setVisibility(vis);
        });
        if (vis) {
            this.getUIElement().removeClass('disabled');
        } else {
            this.getUIElement().addClass('disabled');
        }
        if (this.onChangeVisibleLayers) {
            this.onChangeVisibleLayers();
        }
        return vis;
    }

    _getVisibility() {
        //openlayers still requests images for transparent (opacity===0) layers, so we force hide it
        if (this.locationVisible && this.opacityValue > 0 && this.enabled && this.isVisible()) {
            return true;
        }
        if (!this.locationVisible || this.opacityValue === 0 || !this.enabled || !this.isVisible()) {
            return false;
        }
        return true;
    }

    onNavigation(zoomLevel, centerWgs84, mapExtentWgs84) {
        let locationVisible = true;

        const extentWgs84 = this.layerConfig.getService()
            ? this.layerConfig.getService().getExtentWgs84()
            : undefined;
        if (extentWgs84 && mapExtentWgs84 && !intersects(mapExtentWgs84, extentWgs84)) {
            locationVisible = false;
        }
        const minZoomLevel = this.layerConfig.getMinZoomLevel();
        if (minZoomLevel && zoomLevel < minZoomLevel) {
            locationVisible = false;
        }
        const maxZoomLevel = this.layerConfig.getMaxZoomLevel();
        if (maxZoomLevel && zoomLevel > maxZoomLevel) {
            locationVisible = false;
        }

        if (this.locationVisible !== locationVisible) {
            this.locationVisible = locationVisible;
            this.visibility();
        }

        if (this.legend) {
            this.legend.onNavigation(zoomLevel, centerWgs84, mapExtentWgs84);
        }
    }

    /**
     * Get a UI element to add to the info panel
     * @return {jQuery}
     */
    getSlimUIElement() {
    }

    /**
     * Get a UI element to add to the info modal
     * @return {jQuery}
     */
    getInfoElements() {
    }

    /**
     * Show information about this layer
     * @param modal {Modal} The modal where to show the info in
     * @return {Modal} The shown modal
     */
    showInfo(modal) {
        if (!modal)
            modal = this.infoModal;
        if (modal) {
            modal.setTitle(this.translator.translate('layer.info.modalTitle'));
            modal.setContent(this.getInfoElements());
            modal.show();
            this.logStat("SHOW_INFO");
        }
        return modal;
    }
}

/**
 * A layer from the layer tree
 * @extends AbstractLayer
 */
class Layer extends AbstractLayer {

    static layersByTimeTag = {};

    /**
     *
     * @param translator {Translator} A translator
     * @param isMobileDevice {boolean} true if this is a mobile device. false otherwise
     * @param slimPanel {SlimPanel} A slim panel to display the layers options
     * @param chartModal {Modal} A modal to show the timeseries chart in
     * @param infoModal {Modal} A modal to show indicator information in
     * @param layerConfig {Object} The layer config
     * @param tileGridGetter {function} A function that given a service config, returns a map's TileGrid
     * @param projection {Projection} Map's current projection
     * @param runtimeConfig {RuntimeConfig} The map runtime config
     * @param onChangeRuntimeConfig {function} A callback to be called when runtimeConfig is changed
     * @param exclusive {boolean} Is this node mutually exclusive with its siblings?
     * @param parentName {string} The name of the parent of this layer
     * @param onChangeVisibleLayers {function} A callback to be called when this layer changes visibility
     */
    constructor(translator, isMobileDevice, slimPanel, chartModal, infoModal, layerConfig, tileGridGetter, projection, runtimeConfig, onChangeRuntimeConfig, exclusive = false, parentName = undefined, onChangeVisibleLayers = undefined) {
        super(translator, isMobileDevice, slimPanel, infoModal, layerConfig, tileGridGetter, projection, runtimeConfig, onChangeRuntimeConfig, onChangeVisibleLayers);
        this.chartModal = chartModal;
        this.infoModal = infoModal;
        this.exclusive = exclusive;
        this.parentName = parentName;
        this._updateTimeCalculator();
        this.enabled = true;

        this.styles = {};
        this.styles[this.getCode()] = this.layerConfig.getStyles();
        this._initServices();
    }

    getClassName() {
        return "Layer";
    }

    add2LayersByTimeTag() {
        const timeTag = this.layerConfig.getTimeTag();
        if (timeTag) {
            if (!Layer.layersByTimeTag[timeTag])
                Layer.layersByTimeTag[timeTag] = [];
            Layer.layersByTimeTag[timeTag].push(this);
        }
        return this;
    }

    getRenderOrdinal() {
        return this.layerConfig.getRenderOrdinal();
    }

    getName() {
        return this.getProductConfig().getName(this.getCurrentDate());
    }

    _updateTimeCalculator() {
        const product = this.getProductConfig();
        this.timeCodeCalculator = new TimeCalculator(
            product.getPeriodicity(),
            this.getDataFrom(),
            this.getDataUntil(),
            this.translator
        );
    }

    /**
     * The first day of the last period for that we have data on this product
     * @return {Date}
     */
    getDataUntil() {
        const product = this.getProductConfig();
        const periodicity = product ? product.getPeriodicity() : undefined;
        const timeCalculator = new TimeCalculator(periodicity);
        if (timeCalculator && !timeCalculator.hasNoDate()) {
            const generator = END_DATE_GENERATOR_BY_PRODUCT_CODE[product.getCode()];
            if (generator) {
                let generatedDate = undefined;
                try {
                    generatedDate = generator(LayerConfig.PRODUCT_DATES);
                    if (!generatedDate)
                        this.logError("No date generated")
                } catch (e) {
                    this.logError("Error generating date for " + product.getCode(), e)
                }
                return dateFromStr(new TimeCalculator(product.getPeriodicity()).writeTimeCodeFromDate(generatedDate));
            }
            const res = dateFromStr(timeCalculator.getLatestDate()?timeCalculator.getLatestDate():product.getDataUntil());
            if (!res)
                this.logError("No dataUntil");
            return res;
        }
    }

    /**
     * The first day of the first period for that we have data on this product
     * @return {Date}
     */
    getDataFrom() {
        const product = this.getProductConfig();
        return product ? dateFromStr(product.getDataFrom()) : undefined;
    }

    _initServices() {
        this.services = [];
        const layerServiceConfigs = [this.layerConfig.getService()];
        const drivenServices = this.layerConfig.getDrivenServices();
        for (let ls in drivenServices) {
            layerServiceConfigs.push(drivenServices[ls].getService());
            this.styles[drivenServices[ls].getSrvCode()] = [drivenServices[ls].getStyle()];
        }
        for (let ls in layerServiceConfigs)
            this.services.push(new LayerService(
                this.translator,
                this.isMobileDevice,
                this.runtimeConfig,
                this.onChangeRuntimeConfig,
                this.layerConfig.getClusterSizePixels(),
                layerServiceConfigs[ls],
                this.getProductConfig(),
                this.projection,
                this.tileGridGetter,
                this.layerConfig.getOpacity(),
                this.layerConfig.isVisible(),
                this.getCurrentTimeCode(),
                this.getTimeScale(),
                this.timeCodeCalculator,
                this.getStyleConfig(),
                !!this.getChartType(),
                (featureProps, coords, featureName) => this.showChart(coords, featureProps, featureName)
            ));
    }

    getStyleConfig() {
        return this.styles[this.getCode()][0];
    }

    getAllServiceConfigs() {
        const layerServiceConfigs = [this.layerConfig.getService()];
        this.layerConfig.getDrivenServices().forEach(item => {
            layerServiceConfigs.push(item.getService());
        });
        return layerServiceConfigs;
    }

    /**
     *
     * @return {TimeCalculator}
     */
    getTimeCalculator() {
        return this.timeCodeCalculator;
    }

    /**
     *
     * @param slimPanel {SlimPanel} A slim panel to display the layers options
     * @param projection {Projection} Map's current projection
     * @param runtimeConfig {MapRuntimeConfig} The runtime config
     * @param onChangeRuntimeConfig {function} A callback to be called when runtimeConfig is changed
     * @return {Layer}
     */
    clone(slimPanel, projection, runtimeConfig, onChangeRuntimeConfig) {
        const layerConfig = this.layerConfig.clone();
        layerConfig.override(runtimeConfig);
        return new Layer(
            this.translator,
            this.isMobileDevice,
            slimPanel,
            this.chartModal, this.infoModal,
            layerConfig,
            this.tileGridGetter,
            projection,
            runtimeConfig,
            onChangeRuntimeConfig,
            false,
            undefined,
            undefined
        ).set('cloned', true);
    }

    hasService(code) {
        return super.hasService(code) || this.services.filter(s => s.getCode() === code).length > 0;
    }

    showFeatureInfo(serviceCode, feature, coordinateWgs84, pixelX, pixelY) {
        this.services.filter(s => s.getCode() === serviceCode)[0].showFeatureInfo(feature, coordinateWgs84, pixelX, pixelY);
    }

    hideFeatureInfo(serviceCode) {
        this.services.filter(s => s.getCode() === serviceCode)[0].hideFeatureInfo();
    }

    hideFeatureInfos() {
        this.services.forEach(s => s.hideFeatureInfo());
    }

    /**
     * Get the current time code for this layer
     * @return {string}
     */
    getCurrentTimeCode() {
        if (this.timeCodeCalculator.isLatest())
            return formatDate(this.getDataUntil());
        return this.layerConfig.getTimeCode()
            ? this.layerConfig.getTimeCode()
            : formatDate(this.getDataUntil());
    }

    /**
     * Get the Date for the current time code for this layer
     * @return {Date}
     */
    getCurrentDate() {
        return dateFromStr(this.getCurrentTimeCode());
    }

    /**
     * Get the current time scale for this layer
     * @return {TimeScaleEnum}
     */
    getCurrentTimeScale() {
        return this.getTimeScale() ? this.getTimeScale() : undefined;
    }

    /**
     * The name of this layer, given a timescale
     * @param timeScale {TimeScaleEnum}
     * @return {string}
     */
    getNameForTimeScale(timeScale) {
        return !timeScale
            ? this.getName(this.getCurrentDate())
            : this.layerConfig.getProduct(timeScale).getName(this.getCurrentDate());
    }

    getProductConfig() {
        return this.getProductConfigForTimeScale(this.getCurrentTimeScale());
    }

    /**
     * Get a product config, given a time scale
     * @param timeScale {TimeScaleEnum}
     * @return {ProductConfig}
     */
    getProductConfigForTimeScale(timeScale) {
        return this.layerConfig.getProduct(timeScale);
    }

    /**
     * @param dataSeriesUrl {string} The server dataseries URL
     * @param coordsWgs84 {Array<number>} The coordinates to fetch data for
     * @returns {TimeseriesFetcher}
     */
    getTimeseriesFetcher(dataSeriesUrl, coordsWgs84) {
        if (!this.fetchersMap) {
            this.fetchersMap = {};
        }
        const formattedCoords = formatWgs84(coordsWgs84);
        if (!this.fetchersMap[formattedCoords])
            this.fetchersMap[formattedCoords] = new TimeseriesFetcher(dataSeriesUrl, this, reducePrecisionWgs84(coordsWgs84));
        return this.fetchersMap[formattedCoords];
    }

    /**
     * @param featureProps {Object} The OL Feature properties to get the timeseries for
     * @returns {FeatureTimeseriesFetcher}
     */
    getTimeseriesFetcherForFeature(featureProps) {
        if (!this.fetchersMap) {
            this.fetchersMap = {};
        }
        const idPropName = this.services[0].getIDPropName(featureProps);
        const featureId = featureProps[idPropName];
        if (!this.fetchersMap[featureId])
            this.fetchersMap[featureId] = new FeatureTimeseriesFetcher(this, featureId);
        return this.fetchersMap[featureId];
    }

    getUIElement() {
        if (!this.uiElement) {
            this.uiElement = $(
                '<div class="layer-container layer-code-' + this.getCodeHtmlClass() + '">'
                + '  <div class="layer-header tree-header">'
                + '    <div class="layer-checkbox-container checkbox-container"><input id="layer_' + this.getCodeHtmlClass() + '" value="' + this.getCode() + '" name="' + (this.parentName) + '" type="' + (this.exclusive ? 'radio' : 'checkbox') + '" ' + (this.isVisible() ? 'checked="checked"' : '') + ' /></div>'
                + '    <div class="layer-name"><label class="name" for="layer_' + this.getCodeHtmlClass() + '">' + this.getName() + '</label><div class="layer-time"></div></div>'
                + '    <div class="layer-options"><button tabindex="0" title="' + this.translator.translate('layer.menuButton.name') + '" class="layer-menu-button"></button></div>'
                + '  </div>'
                + '  <div class="layer-body">'
                + '  </div>'
                + '</div>'
            );
            let self = this;
            $('.layer-checkbox-container>input[type=checkbox]', this.uiElement).change(function () {
                const visible = $(this).is(':checked');
                self.logStat(visible ? "SHOW" : "HIDE");
                self.setVisible(visible);
            });
            if (this.getExtentWgs84()) {
                const zoom2ExtentButton = $('<button tabindex="0" title="' + this.translator.translate('layer.zoom2ExtentButton.name') + '" class="layer-zoom2extent-button"></button>');
                $('.layer-options', this.uiElement)
                    .addClass('has-zoom2extent')
                    .prepend(zoom2ExtentButton);
            }

            $('.layer-options>button.layer-menu-button', this.uiElement).click(() => {
                this.showOptionsPanelTop();
                this.logStat("SHOW_OPTIONS");
            });
            this.updateTimeStr();
        }
        return this.uiElement;
    }

    /**
     * Update this layer's visible name
     */
    updateName() {
        const name = this.getName();
        $('.layer-title-slim', this.slimUIElement).empty().html(name);
        $('.layer-name .name', this.uiElement).empty().html(name);
    }

    /**
     * Update this layer's visible time string
     */
    updateTimeStr() {
        if (this.getProductConfig().getPeriodicity() && this.getCurrentTimeCode()) {
            let timeStr = this.getTimeStr();
            if (this.getChartType()) {
                timeStr += '<div class="chartable-img" title="' + this.translator.translate('layer.indicator.chartable') + '"></div>';
            }
            if (this.layerConfig.isPlayable()) {
                timeStr += '<div class="playable-img" title="' + this.translator.translate('layer.indicator.playable') + '"></div>';
            }
            $('.layer-time', this.uiElement).empty().html(timeStr);
            if (this.onChangeVisibleLayers) {
                this.onChangeVisibleLayers();
            }
        }
    }

    /**
     * Get a string of the current time of this layer
     * @return {string|undefined}
     */
    getTimeStr() {
        return this.timeCodeCalculator.timeCodeToString(this.getCurrentTimeCode());
    }

    /**
     * Get a string with the frame of this layer
     * @return {string|undefined}
     */
    getTimeFrameStr() {
        if (!this.timeCodeCalculator)
            return undefined;
        const isTenDailyYearless = this.timeCodeCalculator.isTenDailyYearless() || this.timeCodeCalculator.isTenDailyYearless30Day();
        const isDailyYearless = this.timeCodeCalculator.isDailyYearless();
        const fromStr = isTenDailyYearless
            ? formatDateTenDailyYearless(this.getDataFrom())
            : isDailyYearless ? formatDateDailyYearless(this.getDataFrom()) : formatDate(this.getDataFrom());
        const until = this.timeCodeCalculator.fixDateUntil(this.getDataUntil());
        const untilStr = isTenDailyYearless
            ? formatDateTenDailyYearless(until)
            : isDailyYearless ? formatDateDailyYearless(until) : formatDate(until);
        return this.translator.translate('layer.indicator.time.frame', [
            fromStr,
            untilStr,
            this.timeCodeCalculator.getPeriodicity().getLocalizedName(this.translator)
        ]);
    }

    getNrOfDecimals() {
        const nrOfDecimals = this.layerConfig.getNrOfDecimals();
        return nrOfDecimals ? nrOfDecimals : 0;
    }

    isPlayable() {
        return this.layerConfig.isPlayable();
    }

    setVisible(isVisible) {
        this.runtimeConfig.setLayerVisibility(this.getCode(), isVisible);
        this.onChangeRuntimeConfig();
        this.visibility();
    }

    setOpacity(opacity) {
        super.setOpacity(opacity);
        this.runtimeConfig.setLayerOpacity(this.getCode(), this.opacityValue);
        this.onChangeRuntimeConfig();
    }

    /**
     * Set this layer's time code
     * @param timeCode {string}
     * @param propagate {boolean} if true, will propagate the timecode to the layers with the same time tag
     */
    setTimeCode(timeCode, propagate = true) {
        const fixedTimeCode = this.timeCodeCalculator.fixTimeCode(timeCode);
        if (fixedTimeCode !== this.getCurrentTimeCode()) {
            this.runtimeConfig.setLayerTimeCode(this.getCode(), fixedTimeCode)
            this.onChangeRuntimeConfig();
            for (let ls in this.services)
                this.services[ls].setTimeCode(fixedTimeCode);
            if (this.onChangeVisibleLayers) {
                this.onChangeVisibleLayers();
            }
            this.updateTimeStr();
            this.updateName();
            this.logDebug("SET timecode " + fixedTimeCode);
        }
        if (propagate) {
            const timeTag = this.layerConfig.getTimeTag();
            if (timeTag && Layer.layersByTimeTag[timeTag]) {
                Layer.layersByTimeTag[timeTag].forEach(l => {
                    let middleDateForPeriod = this.getTimeCalculator().getMiddleDateOfPeriod(timeCode);
                    if (this.getTimeCalculator().isYearless()) {
                        const otherTimeCalc = l.getTimeCalculator();
                        middleDateForPeriod = dateFromYearMonthDay(
                            otherTimeCalc.getYearFromDate(l.getCurrentDate()),
                            otherTimeCalc.getMonthFromDate(middleDateForPeriod),
                            otherTimeCalc.getDayFromDate(middleDateForPeriod)
                        );
                    }
                    this.logDebug("Middle date for " + l.getCode() + ": " + formatDate(middleDateForPeriod))
                    l.setTimeCode(formatDate(middleDateForPeriod), false)
                });
            }
        }
    }

    /**
     * This layer's current timescale
     * @return {TimeScaleEnum|undefined}
     */
    getTimeScale() {
        return this.layerConfig.getTimeScale();
    }

    /**
     * Set this layer's time scale
     * @param timeScaleCode {string}
     */
    setTimeScale(timeScaleCode) {
        const prod = this.getProductConfigForTimeScale(TimeScaleEnum.getForCode(timeScaleCode));
        if (prod) {
            this.runtimeConfig.setLayerTimeScale(this.getCode(), timeScaleCode);
            this.onChangeRuntimeConfig();
            this._updateTimeCalculator();
            for (let ls in this.services)
                this.services[ls].setTimeScale(TimeScaleEnum.getForCode(timeScaleCode));

            this.updateName();
        }
    }

    getSlimUIElement() {
        this.slimUIElement = $(
            '<div tabindex="0" class="layer-container-slim">' +
            '   <div class="layer-header-slim">' +
            '       <button type="button" class="layer-info-button" tabindex="0"></button>' +
            '       <div class="layer-title-slim">' + this.getName() + '</div>' +
            '       <div class="layer-options"></div>' +
            '   </div>' +
            '   <div class="layer-time-slim"></div>' +
            '   <div class="layer-legend-slim"></div>' +
            '</div>'
        );
        this.onFocusedEnterOrSpace(() => $('.layer-title-slim', this.slimUIElement).click(), this.slimUIElement);

        if (this.legend) {
            $('.layer-legend-slim', this.slimUIElement).append(this.legend.getSmallGraphic());
        }

        const productConfig = this.getProductConfig();
        if (productConfig.getPeriodicity() && this.getCurrentTimeCode()) {
            $('.layer-time-slim', this.slimUIElement).append(this.getTimeStr());
        } else {
            $('.layer-time-slim', this.slimUIElement).hide();
        }

        if (this.layerConfig.isPlayable()) {
            const playerButton = $('<button tabindex="0" title="' + this.translator.translate('layer.playerButton.name') + '" class="layer-player-button"></button>');
            $('.layer-options', this.slimUIElement).append(playerButton);
        }

        if (this.getExtentWgs84()) {
            const zoom2ExtentButton = $('<button tabindex="0" title="' + this.translator.translate('layer.zoom2ExtentButton.name') + '" class="layer-zoom2extent-button"></button>');
            $('.layer-options', this.slimUIElement).append(zoom2ExtentButton);
        }

        const optionsButton = $('<button tabindex="0" title="' + this.translator.translate('layer.menuButton.name') + '" class="layer-menu-button"></button>');
        $('.layer-options', this.slimUIElement).append(optionsButton);

        optionsButton.click(() => {
            this.showOptionsPanelBottom();
            this.logStat("SHOW_OPTIONS");
        });

        return this.slimUIElement;
    }

    getInfoElements() {
        const elements = $('<div class="layer-info"></div>');
        const layerPropertiesDiv = $('<div class="layer-properties">' +
            '<div class="layer-name"><label>' + this.translator.translate('layer.indicator.label') + '</label><div>' + this.getName() + '</div></div>' +
            '<div class="layer-time"></div>' +
            '</div>');
        const productConfig = this.getProductConfig();
        if (productConfig.getPeriodicity() && this.getCurrentTimeCode()) {
            $('.layer-name>div', layerPropertiesDiv).append(' <span class="context">[' + this.getTimeStr() + ']</span>');
            if (this.hasTimespan()) {
                $('.layer-time', layerPropertiesDiv).append('<label>' + this.translator.translate('layer.indicator.time.label') + '</label><div>' + this.getTimeFrameStr() + '</div>');
            }
        }

        const warningMessages = this.getServiceConfig().getWarningMessages().filter(wm => wm.shouldBeShown());
        if (warningMessages.length > 0) {
            const warningsElem = $('<div class="warnings"><label>' + this.translator.translate('layer.warningList') + '</label><ul></ul></div>');
            layerPropertiesDiv.append(warningsElem);
            warningMessages
                .map(wm => $('<li>' + wm.getMessage() + '</li>'))
                .forEach(li => $('ul', warningsElem).append(li));
        }

        const dataCatalogueUrl = productConfig.getDataCatalogueUrl(this.getCurrentDate());
        if (dataCatalogueUrl) {
            layerPropertiesDiv.append($('<div class="layer-catalog-url"><label>' + dataCatalogueUrl.name + '</label> <a target="_blank" href="' + dataCatalogueUrl.url + '">[' + this.translator.translate('layer.indicator.catalogueUrlTitle') + ']</a></div>'));
        }

        const factsSheetUrl = productConfig.getFactSheetUrl(this.getCurrentDate());
        if (factsSheetUrl && factsSheetUrl.length > 0) {
            layerPropertiesDiv.append($('<div class="fact-sheet-url"><label>' + this.translator.translate('layer.indicator.factSheet.label') + '</label> <a target="_blank" href="' + factsSheetUrl + '">[' + this.translator.translate('layer.indicator.factSheet.open') + ']</a></div>'));
        }

        const license = productConfig.getLicense(this.getCurrentDate());
        if (nnTrim(license)) {
            const licenseElements = $('<div class="layer-license"><label>' + this.translator.translate('layer.indicator.license.label') + '</label> <a class="show-license show-hidden-text">[' + this.translator.translate('layer.indicator.license.show') + ']</a><div class="hidden-text-container license-container">' + license + '</div></div>');
            layerPropertiesDiv.append(licenseElements);
            $('.show-license', licenseElements).click(function () {
                $('.license-container', licenseElements).toggle()
            });
        }

        const citationStatement = productConfig.getCitationStatement(this.getCurrentDate());
        if (nnTrim(citationStatement)) {
            const citationStatementElements = $('<div class="layer-citation-statement"><label>' + this.translator.translate('layer.indicator.citationStatement.label') + '</label> <a class="show-citation-statement show-hidden-text">[' + this.translator.translate('layer.indicator.citationStatement.show') + ']</a><div class="hidden-text-container citation-statement-container">' + citationStatement + '</div></div>');
            layerPropertiesDiv.append(citationStatementElements);
            $('.show-citation-statement', citationStatementElements).click(function () {
                $('.citation-statement-container', citationStatementElements).toggle()
            });
        }

        const publicationTitle = productConfig.getPublicationTitle(this.getCurrentDate());
        if (nnTrim(publicationTitle)) {
            const publicationUrl = productConfig.getPublicationUrl(this.getCurrentDate());
            const titleElement = publicationUrl && publicationUrl.trim().length > 0
                ? $('<p>' + publicationTitle + '<a target="_blank" href="' + publicationUrl + '"> [' + this.translator.translate('layer.indicator.refPublication.open') + ']</a></p>')
                : $('<p>' + publicationTitle + '</p>');
            const publicationAuthors = productConfig.getPublicationAuthors(this.getCurrentDate());
            const publicationYear = productConfig.getPublicationYear(this.getCurrentDate());
            const publicationPublisher = productConfig.getPublicationPublisher(this.getCurrentDate());
            const authorsElement = publicationAuthors && publicationAuthors.trim().length > 0
                ? $('<p>' + publicationAuthors + (publicationYear ? ' [' + publicationYear + '] ' : '') + (publicationPublisher && publicationPublisher.trim().length > 0 ? '(' + publicationPublisher + ')' : '') + '</p>')
                : undefined;
            const publicationElements = $(
                '<div class="layer-ref-publication">' +
                '   <label>' + this.translator.translate('layer.indicator.refPublication.label') + '</label> ' +
                '   <a class="show-ref-publication show-hidden-text">[' + this.translator.translate('layer.indicator.refPublication.show') + ']</a>' +
                '   <div class="hidden-text-container ref-publication-container"></div>' +
                '</div>');
            $('.ref-publication-container', publicationElements).append(titleElement);
            if (authorsElement)
                $('.ref-publication-container', publicationElements).append(authorsElement)
            layerPropertiesDiv.append(publicationElements);
            $('.show-ref-publication', publicationElements).click(() => $('.ref-publication-container', publicationElements).toggle());
        }

        const organisationName = productConfig.getOrganisationName(this.getCurrentDate());
        const contactPerson = productConfig.getContactPerson(this.getCurrentDate());
        const email = productConfig.getEmail(this.getCurrentDate());
        let contact = '';
        if (nnTrim(contactPerson))
            contact += contactPerson;
        if (nnTrim(organisationName))
            contact += ((contact.length > 0 ? ', ' : '') + organisationName);
        if (nnTrim(email))
            contact += ((contact.length > 0 ? ' (' : '') + email + (contact.length > 0 ? ')' : ''));
        if (nnTrim(contact)) {
            const contactElements = $('<div class="layer-contact"><label>' + this.translator.translate('layer.indicator.contact.label') + '</label> <a class="show-contact show-hidden-text">[' + this.translator.translate('layer.indicator.contact.show') + ']</a><div class="hidden-text-container contact-container">' + contact + '</div></div>');
            layerPropertiesDiv.append(contactElements);
            $('.show-contact', contactElements).click(() => $('.contact-container', contactElements).toggle());
        }

        elements.append(layerPropertiesDiv);

        elements.append($('<div class="layer-info-legend"></div>'));
        const largeGraphic = this.legend ? this.legend.getLargeGraphic() : undefined;
        if (largeGraphic) {
            let legendContainer = $('<div class="layer-legend"><div class="info-title">' + this.translator.translate('layer.indicator.legend') + '</div></div>');
            legendContainer.append(largeGraphic);
            $('.layer-info-legend', elements).append(legendContainer);
        }
        const info = productConfig.getInfo(this.getCurrentDate());
        if (info) {
            const infoElements = $('<div class="layer-info"><div class="info-title">' + this.translator.translate('layer.indicator.info') + '</div>' + info + '</div>');
            $('a', infoElements).attr('target', '_blank');
            $('.layer-info-legend', elements).append(infoElements)
        }
        return elements;
    }

    /**
     * Get this layer if visible
     * @return {Array<Layer>}
     */
    getVisibleLayers() {
        return this._getVisibility() ? [this] : [];
    }

    /**
     * Get this layer
     * @return {Array<Layer>}
     */
    getLayers() {
        return [this];
    }

    /**
     * Show the options panel for this layer
     * @param absolutePosition {object} The position where to show the panel {top: integer, left: integer}
     */
    showOptionsPanel(absolutePosition) {
        this._showOptionsPanel(absolutePosition, false, false);
    }

    /**
     * Show the options panel in the top left of the screen
     */
    showOptionsPanelTop() {
        this._showOptionsPanel(undefined, true, false);
    }

    /**
     * Show the options panel in the bottom left of the screen
     */
    showOptionsPanelBottom() {
        this._showOptionsPanel(undefined, false, true);
    }

    _showOptionsPanel(absolutePosition, isTop, isBottom) {
        const container = $('<div></div>');
        this.slimPanel.setContent(container);
        this.slimPanel.setTitle(this.translator.translate('layer.menuButton.name') + ' - ' + this.getName());

        const timeScales = this.layerConfig.getService().getTimeScales();
        if (timeScales.length > 0) {
            const timeScaleDropdownId = this.getCodeHtmlClass() + '_timeScale_dropdown';
            const timeScaleContainer = $(
                '<div class="dropdown-container time-scale-container">' +
                '   <label for="' + timeScaleDropdownId + '">' + this.translator.translate('layer.timescale.dropdown.label') + '</label>' +
                '</div>'
            );
            const timeScaleOptions = timeScales.map(ts => {
                return new DropdownOption(this.translator, this.isMobileDevice, ts.getLocalizationKey(), ts.getCode(), undefined)
            });
            const initialValue = this.getTimeScale() ? this.getTimeScale().getCode() : timeScales[0].getCode();
            this.timeScaleDropdown = new Dropdown(this.translator, this.isMobileDevice, timeScaleDropdownId, timeScaleOptions, initialValue, true, undefined,
                dropDown => this.setTimeScale(dropDown.getValue()), undefined);
            timeScaleContainer.append(this.timeScaleDropdown.getUIElement());
            container.append(timeScaleContainer);
        }

        if (this.hasTimespan()) {
            const timeCode = this.getCurrentTimeCode();

            if (!this.timeCodeCalculator.isYearless()) {
                const yearSliderId = this.getCodeHtmlClass() + '_year_slider';
                const minYear = this.timeCodeCalculator.getMinYear();
                const maxYear = this.timeCodeCalculator.getMaxYear();
                const currentYear = this.timeCodeCalculator.parseYear(timeCode);
                this.yearSlider = new Slider(
                    this.translator,
                    this.isMobileDevice,
                    yearSliderId,
                    minYear, maxYear, currentYear, 1, maxYear - minYear + 1,
                    undefined, undefined,
                    slider => {
                        const year = slider.getValue();
                        const currentTimeCode = this.getCurrentTimeCode();
                        if (this.timeCodeCalculator.isYearly()) {
                            this.setTimeCode(this.timeCodeCalculator.writeYearly(year, true));
                        } else if (this.timeCodeCalculator.isMonthly()) {
                            this.setTimeCode(this.timeCodeCalculator.writeMonthly(year, this.timeCodeCalculator.parseMonth(currentTimeCode), true));
                        } else if (this.timeCodeCalculator.isMonthlyForecast()) {
                            this.setTimeCode(this.timeCodeCalculator.writeMonthlyForecast(year, this.timeCodeCalculator.parseMonth(currentTimeCode), true));
                        } else if (this.timeCodeCalculator.isTenDaily() || this.timeCodeCalculator.isMonthlyUpdatedTenDaily()) {
                            this.setTimeCode(this.timeCodeCalculator.writeTenDaily(year, this.timeCodeCalculator.parseMonth(currentTimeCode), this.timeCodeCalculator.parseTenDays(currentTimeCode), true));
                        } else if (this.timeCodeCalculator.isWeekly()) {
                            this.setTimeCode(this.timeCodeCalculator.writeWeekly(year, this.timeCodeCalculator.parseWeek(currentTimeCode), true));
                        } else if (this.timeCodeCalculator.isISOWeekly()) {
                            this.setTimeCode(this.timeCodeCalculator.writeISOWeekly(year, this.timeCodeCalculator.parseISOWeek(currentTimeCode), true));
                        } else if (this.timeCodeCalculator.isDaily()) {
                            this.setTimeCode(this.timeCodeCalculator.writeDaily(year, this.timeCodeCalculator.parseMonth(currentTimeCode), this.timeCodeCalculator.parseDay(currentTimeCode), true));
                        }
                    }, true, true
                );
                let yearContainer = $(
                    '<div class="slider-container time-container">' +
                    '   <label for="' + yearSliderId + '">' + this.translator.translate('layer.year.slider.label') + '</label>' +
                    '</div>'
                ).append(this.yearSlider.getUIElement());
                container.append(yearContainer);
            }

            if (this.timeCodeCalculator.isWeekly() || this.timeCodeCalculator.isISOWeekly()) {
                const isISOWeekly = this.timeCodeCalculator.isISOWeekly();
                const weekSliderId = this.getCodeHtmlClass() + '_week_slider';
                const weekContainer = $(
                    '<div class="slider-container time-container">' +
                    '   <label for="' + weekSliderId + '">' + this.translator.translate(isISOWeekly ? 'layer.ISOWeek.slider.label' : 'layer.week.slider.label') + '</label>' +
                    '</div>'
                );

                const onChangeYearWeekly = () => {
                    const year = this.yearSlider.getValue();
                    const minWeek = isISOWeekly ? this.timeCodeCalculator.getMinISOWeekForYear(year) : this.timeCodeCalculator.getMinWeekForYear(year);
                    const maxWeek = isISOWeekly ? this.timeCodeCalculator.getMaxISOWeekForYear(year) : this.timeCodeCalculator.getMaxWeekForYear(year);
                    const currentWeek = this.weekSlider
                        ? Math.max(minWeek, Math.min(maxWeek, this.weekSlider.getValue()))
                        : Math.max(minWeek, Math.min(maxWeek, isISOWeekly ? this.timeCodeCalculator.parseISOWeek(this.getCurrentTimeCode()) : this.timeCodeCalculator.parseWeek(this.getCurrentTimeCode())));

                    const parsedYear = this.timeCodeCalculator.parseYear(this.getCurrentTimeCode());
                    this.setTimeCode(isISOWeekly ? this.timeCodeCalculator.writeISOWeekly(parsedYear, currentWeek, true) : this.timeCodeCalculator.writeWeekly(parsedYear, currentWeek, true));
                    this.weekSlider = new Slider(
                        this.translator,
                        this.isMobileDevice,
                        weekSliderId,
                        minWeek, maxWeek, currentWeek, 1, maxWeek - minWeek,
                        undefined, value => {
                            const year = this.yearSlider.getValue();
                            const fstDateStr = isISOWeekly ? this.timeCodeCalculator.writeISOWeekly(year, value, true) : this.timeCodeCalculator.writeWeekly(year, value, true);
                            const lstDate = this.timeCodeCalculator.addDays(dateFromStr(fstDateStr), 6);
                            const lstDateStr = this.timeCodeCalculator.writeDaily(this.timeCodeCalculator.getYearFromDate(lstDate),
                                this.timeCodeCalculator.getMonthFromDate(lstDate), this.timeCodeCalculator.getDayFromDate(lstDate), true);
                            return $('<div class="week-slider-legend"><div>' + fstDateStr + '</div><div>' + lstDateStr + '</div></div>');
                        },
                        slider => {
                            let week = slider.getValue();
                            const currentTimeCode = this.getCurrentTimeCode();
                            const parsedYear = this.timeCodeCalculator.parseYear(currentTimeCode);
                            this.setTimeCode(isISOWeekly ? this.timeCodeCalculator.writeISOWeekly(parsedYear, week, true) : this.timeCodeCalculator.writeWeekly(parsedYear, week, true));
                        }, true, true, this.yearSlider,
                        parentValue => isISOWeekly ? this.timeCodeCalculator.getMaxISOWeekForYear(parentValue) : this.timeCodeCalculator.getMaxWeekForYear(parentValue),
                        parentValue => isISOWeekly ? this.timeCodeCalculator.getMinISOWeekForYear(parentValue) : this.timeCodeCalculator.getMinWeekForYear(parentValue)
                    );
                    $('#' + weekSliderId).remove();
                    weekContainer.append(this.weekSlider.getUIElement());
                    this.yearSlider.onChange(onChangeYearWeekly);
                };

                onChangeYearWeekly();
                container.append(weekContainer);
            } else if (this.timeCodeCalculator.isMonthly() || this.timeCodeCalculator.isMonthlyForecast() || this.timeCodeCalculator.isDaily() || this.timeCodeCalculator.isDailyYearless()
                || this.timeCodeCalculator.isTenDaily() || this.timeCodeCalculator.isMonthlyUpdatedTenDaily() || this.timeCodeCalculator.isTenDailyYearless() || this.timeCodeCalculator.isTenDailyYearless30Day()) {
                const monthSliderId = this.getCodeHtmlClass() + '_month_slider';
                const monthContainer = $(
                    '<div class="slider-container time-container">' +
                    '   <label for="' + monthSliderId + '">' + this.translator.translate('layer.month.slider.label') + '</label>' +
                    '</div>'
                );

                const onChangeYearMonthly = () => {
                    const minMonth = this.timeCodeCalculator.getMinMonthForYear(this.yearSlider ? this.yearSlider.getValue() : 0);
                    const maxMonth = this.timeCodeCalculator.getMaxMonthForYear(this.yearSlider ? this.yearSlider.getValue() : 0);
                    const currentMonth = this.monthSlider
                        ? Math.max(minMonth, Math.min(maxMonth, this.monthSlider.getValue()))
                        : Math.max(minMonth, Math.min(maxMonth, this.timeCodeCalculator.parseMonth(this.getCurrentTimeCode())));

                    const setTimeCode = month => {
                        const currentTimeCode = this.getCurrentTimeCode();
                        if (this.timeCodeCalculator.isMonthly()) {
                            this.setTimeCode(this.timeCodeCalculator.writeMonthly(this.timeCodeCalculator.parseYear(currentTimeCode), month, true));
                        } else if (this.timeCodeCalculator.isMonthlyForecast()) {
                            this.setTimeCode(this.timeCodeCalculator.writeMonthlyForecast(this.timeCodeCalculator.parseYear(currentTimeCode), month, true));
                        } else if (this.timeCodeCalculator.isTenDaily() || this.timeCodeCalculator.isMonthlyUpdatedTenDaily() || this.timeCodeCalculator.isTenDailyYearless() || this.timeCodeCalculator.isTenDailyYearless30Day()) {
                            this.setTimeCode(this.timeCodeCalculator.writeTenDaily(this.timeCodeCalculator.parseYear(currentTimeCode), month, this.timeCodeCalculator.parseTenDays(currentTimeCode), true));
                        } else if (this.timeCodeCalculator.isDaily() || this.timeCodeCalculator.isDailyYearless()) {
                            this.setTimeCode(this.timeCodeCalculator.writeDaily(this.timeCodeCalculator.parseYear(currentTimeCode), month, this.timeCodeCalculator.parseDay(currentTimeCode), true));
                        }
                    };

                    setTimeCode(currentMonth);

                    this.monthSlider = new Slider(
                        this.translator,
                        this.isMobileDevice,
                        monthSliderId,
                        minMonth, maxMonth, currentMonth, 1, maxMonth - minMonth + 1,
                        undefined, undefined,
                        slider => setTimeCode(slider.getValue()), true, true, this.yearSlider,
                        parentValue => this.timeCodeCalculator.getMaxMonthForYear(parentValue),
                        parentValue => this.timeCodeCalculator.getMinMonthForYear(parentValue)
                    );
                    $('#' + monthSliderId).remove();
                    monthContainer.append(this.monthSlider.getUIElement());
                    if (this.yearSlider)
                        this.yearSlider.onChange(onChangeYearMonthly);
                };

                onChangeYearMonthly();
                container.append(monthContainer);

                if (this.timeCodeCalculator.isTenDaily() || this.timeCodeCalculator.isMonthlyUpdatedTenDaily() || this.timeCodeCalculator.isTenDailyYearless() || this.timeCodeCalculator.isTenDailyYearless30Day()) {
                    const tenDaySliderId = this.getCodeHtmlClass() + '_tenDay_slider';
                    const tenDayContainer = $(
                        '<div class="slider-container time-container">' +
                        '   <label for="' + tenDaySliderId + '">' + this.translator.translate('layer.tenday.slider.label') + '</label>' +
                        '</div>'
                    );

                    const onChangeMonthYearTenDaily = () => {
                        const min = this.timeCodeCalculator.getMinTenDaysForYearMonth(this.yearSlider ? this.yearSlider.getValue() : 0, this.monthSlider.getValue());
                        const max = this.timeCodeCalculator.getMaxTenDaysForYearMonth(this.yearSlider ? this.yearSlider.getValue() : 0, this.monthSlider.getValue());
                        const currentTenDay = this.tenDaySlider
                            ? Math.max(min, Math.min(max, this.tenDaySlider.getValue()))
                            : Math.max(min, Math.min(max, this.timeCodeCalculator.parseDay(this.getCurrentTimeCode())));

                        this.setTimeCode(this.timeCodeCalculator.writeTenDaily(this.timeCodeCalculator.parseYear(this.getCurrentTimeCode()), this.timeCodeCalculator.parseMonth(this.getCurrentTimeCode()), currentTenDay, true));

                        this.tenDaySlider = new Slider(
                            this.translator,
                            this.isMobileDevice,
                            tenDaySliderId,
                            min, max, currentTenDay, 1, max - min + 1,
                            undefined,
                            undefined,
                            slider => {
                                const tenDay = slider.getValue();
                                const currentTimeCode = this.getCurrentTimeCode();
                                this.setTimeCode(this.timeCodeCalculator.writeTenDaily(this.timeCodeCalculator.parseYear(currentTimeCode), this.timeCodeCalculator.parseMonth(currentTimeCode), tenDay, true));
                            }, true, true, this.monthSlider,
                            parentValue => this.timeCodeCalculator.getMaxTenDaysForYearMonth(this.yearSlider ? this.yearSlider.getValue() : 0, parentValue),
                            parentValue => this.timeCodeCalculator.getMinDayForYearMonth(this.yearSlider ? this.yearSlider.getValue() : 0, parentValue)
                        );
                        $('#' + tenDaySliderId).remove();
                        tenDayContainer.append(this.tenDaySlider.getUIElement());
                        if (this.yearSlider)
                            this.yearSlider.onChange(onChangeMonthYearTenDaily);
                        this.monthSlider.onChange(onChangeMonthYearTenDaily);
                    };

                    onChangeMonthYearTenDaily();
                    container.append(tenDayContainer);
                } else if (this.timeCodeCalculator.isDaily() || this.timeCodeCalculator.isDailyYearless()) {
                    const daySliderId = this.getCodeHtmlClass() + '_day_slider';
                    let dayContainer = $(
                        '<div class="slider-container time-container">' +
                        '   <label for="' + daySliderId + '">' + this.translator.translate('layer.day.slider.label') + '</label>' +
                        '</div>'
                    );

                    const onChangeMonthYearDaily = () => {
                        const year = this.yearSlider ? this.yearSlider.getValue() : undefined;
                        const min = this.timeCodeCalculator.getMinDayForYearMonth(year, this.monthSlider.getValue());
                        const max = this.timeCodeCalculator.getMaxDayForYearMonth(year, this.monthSlider.getValue());
                        const currentDay = this.daySlider
                            ? Math.max(min, Math.min(max, this.daySlider.getValue()))
                            : Math.max(min, Math.min(max, this.timeCodeCalculator.parseDay(this.getCurrentTimeCode())));

                        this.setTimeCode(this.timeCodeCalculator.writeDaily(this.timeCodeCalculator.parseYear(this.getCurrentTimeCode()), this.timeCodeCalculator.parseMonth(this.getCurrentTimeCode()), currentDay, true));
                        this.daySlider = new Slider(
                            this.translator,
                            this.isMobileDevice,
                            daySliderId,
                            min, max, currentDay, 1, max - min + 1, undefined, undefined,
                            slider => {
                                const day = slider.getValue();
                                const currentTimeCode = this.getCurrentTimeCode();
                                this.setTimeCode(this.timeCodeCalculator.writeDaily(this.timeCodeCalculator.parseYear(currentTimeCode), this.timeCodeCalculator.parseMonth(currentTimeCode), day, true));
                            }, true, true, this.monthSlider,
                            parentValue => this.timeCodeCalculator.getMaxDayForYearMonth(year, parentValue),
                            parentValue => this.timeCodeCalculator.getMinDayForYearMonth(year, parentValue)
                        );
                        $('#' + daySliderId).remove();
                        dayContainer.append(this.daySlider.getUIElement());
                        if (this.yearSlider)
                            this.yearSlider.onChange(onChangeMonthYearDaily);
                        this.monthSlider.onChange(onChangeMonthYearDaily);
                    };
                    onChangeMonthYearDaily();
                    container.append(dayContainer);
                }
            }
        }
        const opacitySliderId = this.getCodeHtmlClass() + '_opacity_slider';
        this.opacitySlider = new Slider(
            this.translator,
            this.isMobileDevice,
            opacitySliderId,
            0.2, 1, this.layerConfig.getOpacity(), 0.01, 9,
            value => (value * 100).toFixed(0) + '%',
            undefined,
            slider => this.setOpacity(slider.getValue()),
            false, false
        );
        const opacityContainer = $(
            '<div class="slider-container opacity-container">' +
            '   <label for="' + opacitySliderId + '">' + this.translator.translate('layer.opacity.slider.label') + '</label>' +
            '</div>'
        ).append(this.opacitySlider.getUIElement());
        container.append(opacityContainer);

        if (absolutePosition) {
            const width = this.slimPanel.getUIElement().width() + this.containerAbsolutePosition.left;
            const height = this.slimPanel.getUIElement().height() + this.containerAbsolutePosition.top;
            const mapWidth = this.containerWidth + this.containerAbsolutePosition.left;
            const mapHeight = this.containerHeight + this.containerAbsolutePosition.top;
            absolutePosition.left = absolutePosition.left + width + 2 > mapWidth - 2
                ? mapWidth - width - 10
                : absolutePosition.left + 2;
            absolutePosition.top = absolutePosition.top + height > mapHeight
                ? mapHeight - height - 10
                : absolutePosition.top;
            this.slimPanel.show(absolutePosition.top, absolutePosition.left);
        } else if (isTop) {
            this.slimPanel.showTop();
        } else if (isBottom) {
            this.slimPanel.showBottom();
        }
    }

    /**
     * Does this layer have time?
     * @return {boolean}
     */
    hasTime() {
        return this.getDataFrom() && this.getDataUntil() && !this.timeCodeCalculator.isUnknown();
    }

    /**
     * Does this layer have timespan?
     * @return {boolean}
     */
    hasTimespan() {
        return this.hasTime() && !this.timeCodeCalculator.isLatest() && this.getDataFrom() < this.getDataUntil();
    }

    /**
     * Get the chart type for this layer
     * @return {IndicatorChartTypeEnum}
     */
    getChartType() {
        return this.layerConfig.getChartType();
    }

    showChart(coordinates, featureProps = undefined, featureName = undefined, dataSeriesUrl = undefined, chartModal = undefined, infoModal = undefined) {
        if (!chartModal)
            chartModal = this.chartModal;
        if (!infoModal)
            infoModal = this.infoModal;

        const timeCalculator = this.getTimeCalculator();
        const currentTime = dateFromStr(this.getCurrentTimeCode());

        const from = currentTime
            ? timeCalculator.fixDate(timeCalculator.addDays(currentTime, -365))
            : undefined;
        const until = currentTime;

        const datePicker = new DatePicker('chart_date_picker_' + this.getCode(), this.translator, [this], timeCalculator.getPeriodicity(), this.runtimeConfig, this.onChangeRuntimeConfig, this.isMobileDevice);
        datePicker.setDefaultFromUntil(from, until);
        const datePickerElement = datePicker.getUIElement();
        const indicatorChart = featureProps
            ? new FeatureIndicatorChart('indicator_chart_' + this.getCode(), featureName, this.translator, this, infoModal, this.runtimeConfig, this.isMobileDevice)
            : new IndicatorChart('indicator_chart_' + this.getCode(), this.translator, dataSeriesUrl, this, infoModal, this.runtimeConfig, this.isMobileDevice);
        const charElement = indicatorChart.getUIElement();

        const onChangeDates = picker => indicatorChart.setFromUntilAndCoords(picker.getFrom(), picker.getUntil(), coordinates, featureProps);
        datePicker.onChange(onChangeDates);

        chartModal.setTitle(this.translator.translate('layer.indicator.chartTitle', [formatWgs84(coordinates)]));
        const content = $('<div><ul></ul></div>');
        chartModal.setContent(content);
        $('ul', content).addClass('container-list').append(datePickerElement).append(charElement);

        datePicker.onAdd2DOM();
        indicatorChart.onAdd2DOM();

        chartModal.show();
        onChangeDates(datePicker);
    }

    showInfo(modal) {
        const openModal = super.showInfo(modal);
        if (openModal)
            openModal.setTitle(this.translator.translate('layer.info.modalTitleIndicator'));
        return openModal;
    }
}

/**
 * A base layer
 * @extends AbstractLayer
 */
class BaseLayer extends AbstractLayer {

    /**
     *
     * @param translator {Translator} A translator
     * @param isMobileDevice {boolean} true if this is a mobile device. false otherwise
     * @param slimPanel {SlimPanel} A slim panel to display the layers options
     * @param infoModal {Modal} A modal to show indicator information in
     * @param layerConfig {Object} The layer config
     * @param tileGridGetter {function} A function that given a service config, returns a map's TileGrid
     * @param projection {Projection} Map's current projection
     * @param runtimeConfig {RuntimeConfig} The map runtime config
     * @param onChangeRuntimeConfig {function} A callback to be called when runtimeConfig is changed
     * @param onChangeVisibleLayers {function} A callback to be called when this layer changes visibility
     * @param mapBackgroundColorSetter {function} A callback to change the map background color, when the layer becomes visible
     */
    constructor(translator, isMobileDevice, slimPanel, infoModal, layerConfig, tileGridGetter, projection, runtimeConfig, onChangeRuntimeConfig, onChangeVisibleLayers, mapBackgroundColorSetter) {
        super(translator, isMobileDevice, slimPanel, infoModal, layerConfig, tileGridGetter, projection, runtimeConfig, onChangeRuntimeConfig, onChangeVisibleLayers);
        this.mapBackgroundColorSetter = mapBackgroundColorSetter;
        this._initServices();
    }

    getClassName() {
        return "BaseLayer";
    }

    _initServices() {
        this.services = [];
        this.services.push(new BaseLayerService(this.layerConfig.getService(), this.isMobileDevice, this.layerConfig.getProduct(), this.projection, this.tileGridGetter, 1, this.layerConfig.isVisible(), this.runtimeConfig));
    }

    /**
     *
     * @param slimPanel {SlimPanel} A slim panel to display the layers options
     * @param projection {Projection} Map's current projection
     * @param runtimeConfig {MapRuntimeConfig} The runtime config
     * @param onChangeRuntimeConfig {function} A callback to be called when runtimeConfig is changed
     * @param mapBackgroundColorSetter {function} A callback to set the background color of the map
     * @return {BaseLayer}
     */
    clone(slimPanel, projection, runtimeConfig, onChangeRuntimeConfig, mapBackgroundColorSetter) {
        const layerConfig = this.layerConfig.clone();
        layerConfig.override(runtimeConfig);
        return new BaseLayer(
            this.translator,
            this.isMobileDevice,
            slimPanel,
            this.infoModal,
            layerConfig,
            this.tileGridGetter,
            projection,
            runtimeConfig,
            onChangeRuntimeConfig,
            undefined,
            mapBackgroundColorSetter
        ).set('cloned', true);
    }

    getUIElement() {
        if (!this.uiElement) {
            this.uiElement = $(
                '<div role="listitem" class="base-layer-container base-layer-code-' + this.getCodeHtmlClass() + '">'
                + '  <div class="base-layer-header tree-header">'
                + '     <div class="base-layer-checkbox-container checkbox-container"><input tabindex="0" id="base_layer_' + this.getCodeHtmlClass() + '" value="' + this.getCode() + '" name="base_layer" type="radio" ' + (this.isVisible() ? 'checked="checked"' : '') + ' /></div>'
                + '     <div class="base-layer-name"><label for="base_layer_' + this.getCodeHtmlClass() + '">' + this.getName() + '</label></div>'
                + '  </div>'
                + '  <div class="base-layer-body"></div>'
                + '</div>'
            );
        }
        return this.uiElement;
    }

    getInfoElements() {
        const elements = $('<div class="layer-info"></div>');
        const layerProperties = $(
            '<div class="layer-properties">' +
            '<div class="layer-name"><label>' + this.translator.translate(this.getLabelKey()) + '</label> ' + this.getName() + '</div>' +
            '</div>');
        elements.append(layerProperties);

        elements.append($('<div class="layer-info-legend"></div>'));
        const largeGraphic = this.legend ? this.legend.getLargeGraphic() : undefined;
        if (largeGraphic) {
            let legendContainer = $('<div class="layer-legend"><div class="info-title">' + this.translator.translate('layer.indicator.legend') + '</div></div>');
            legendContainer.append(largeGraphic);
            $('.layer-info-legend', elements).append(legendContainer);
        } else {
            elements.addClass('no-legend');
        }

        const productConfig = this.getProductConfig();
        const infoElements = $('<div class="layer-info"><div class="info-title">' + this.translator.translate('layer.indicator.info') + '</div>' + productConfig.getInfo() + '</div>');
        $('a', infoElements).attr('target', '_blank');
        $('.layer-info-legend', elements).append(infoElements)

        return elements;
    }

    /**
     * Get the translatable label key for base layers
     * @return {string}
     */
    getLabelKey() {
        return 'base.layer.label';
    }

    getSlimUIElement() {
        this.slimUIElement = $(
            '<div tabindex="0" class="layer-container-slim">' +
            '   <div class="layer-header-slim">' +
            '       <button type="button" class="layer-info-button" tabindex="0"></button>' +
            '       <div class="layer-title-slim">' + this.getName() + '</div>' +
            '   </div>' +
            '</div>'
        );
        this.onFocusedEnterOrSpace(() => $('.layer-title-slim', this.slimUIElement).click(), this.slimUIElement);
        if (this.legend) {
            const legendContainer = $('<div class="layer-legend-slim"></div>')
                .append(this.legend.getSmallGraphic());
            this.slimUIElement.append(legendContainer);
        } else {
            const info = this.getProductConfig().getInfo();
            if (info) {
                const infoContainer = $('<div class="layer-info-slim"></div>')
                    .append(info.crop(100));
                $('a', infoContainer).attr('target', '_blank');

                this.slimUIElement.append(infoContainer);
            }
        }
        return this.slimUIElement;
    }

    setVisible(isVisible) {
        this.runtimeConfig.setBaseLayerVisibility(this.getCode(), isVisible);
        this.onChangeRuntimeConfig();
        this.visibility();
    }

    visibility(visible) {
        const visibility = super.visibility(visible);
        if (visibility && this.mapBackgroundColorSetter) {
            this.mapBackgroundColorSetter(this.layerConfig.getMapBackgroundColor());
        }
        return visibility;
    }
}

/**
 * A context layer
 * @extends BaseLayer
 * @hideconstructor
 */
class ContextLayer extends BaseLayer {

    constructor(translator, isMobileDevice, slimPanel, infoModal, layerConfig, tileGridGetter, projection, runtimeConfig, onChangeRuntimeConfig, onChangeVisibleLayers) {
        super(translator, isMobileDevice, slimPanel, infoModal, layerConfig, tileGridGetter, projection, runtimeConfig, onChangeRuntimeConfig, onChangeVisibleLayers, undefined)
    }

    getClassName() {
        return "ContextLayer";
    }

    _initServices() {
        this.services = [];
        this.services.push(new Service(this.layerConfig.getService(), this.isMobileDevice, this.layerConfig.getProduct(), this.projection, this.tileGridGetter, 1, this.layerConfig.isVisible(), this.runtimeConfig));
    }

    /**
     *
     * @param slimPanel {SlimPanel} A slim panel to display the layers options
     * @param projection {Projection} Map's current projection
     * @param runtimeConfig {MapRuntimeConfig} The runtime config
     * @param onChangeRuntimeConfig {function} A callback to be called when runtimeConfig is changed
     * @return {ContextLayer}
     */
    clone(slimPanel, projection, runtimeConfig, onChangeRuntimeConfig) {
        const layerConfig = this.layerConfig.clone();
        layerConfig.override(runtimeConfig);
        return new ContextLayer(
            this.translator,
            this.isMobileDevice,
            slimPanel,
            this.infoModal,
            layerConfig,
            this.tileGridGetter,
            projection,
            runtimeConfig,
            onChangeRuntimeConfig,
            undefined
        ).set('cloned', true);
    }

    getUIElement() {
        if (!this.uiElement) {
            this.uiElement = $(
                '<div role="listitem" class="context-layer-container context-layer-code-' + this.getCodeHtmlClass() + '">'
                + '  <div class="context-layer-header tree-header">'
                + '     <div class="context-layer-checkbox-container checkbox-container"><input  tabindex="0" id="context_layer_' + this.getCodeHtmlClass() + '" value="' + this.getCode() + '" name="context_layer" type="checkbox" ' + (this.isVisible() ? 'checked="checked"' : '') + ' /></div>'
                + '     <div class="context-layer-name"><label for="context_layer_' + this.getCodeHtmlClass() + '">' + this.getName() + '</label></div>'
                + '  </div>'
                + '  <div class="context-layer-body"></div>'
                + '</div>'
            );
            const self = this;
            $('.context-layer-checkbox-container>input[type=checkbox]', this.uiElement).change(function () {
                const visible = $(this).is(':checked');
                self.setVisible(visible);
                self.logStat(visible ? "SHOW" : "HIDE");
            });
        }

        return this.uiElement;
    }

    /**
     * Get the translatable label key for context layers
     * @return {string}
     */
    getLabelKey() {
        return 'context.layer.label';
    }

    setVisible(isVisible) {
        this.runtimeConfig.setContextLayerVisibility(this.getCode(), isVisible);
        this.onChangeRuntimeConfig();
        this.visibility(isVisible);
    }
}


initNamespace();

const FEATURE_PARSER_BY_SERVICE_CODE = {}
const URL_PARAMS_GENERATOR_BY_SERVICE_CODE = {}
const END_DATE_GENERATOR_BY_PRODUCT_CODE = {}

/**
 * Register a feature parser, to parse the feature and return the relevant properties for information for the user
 * @param serviceCode {string} The code of a vector service
 * @param parser {FeatureParser} The parser for the layer corresponding to the service code
 */
function registerFeatureParser(serviceCode, parser) {
    FEATURE_PARSER_BY_SERVICE_CODE[serviceCode] = parser;
}

/**
 * Register a URL generator, to generate a layer URL given current layer time and current layer timescale
 * @param serviceCode {string} The code of a layer service
 * @param generator {function} A function that takes Date and a timescale code (string) and returns a map of param name to param value
 */
function registerUrlParamsGenerator(serviceCode, generator) {
    URL_PARAMS_GENERATOR_BY_SERVICE_CODE[serviceCode] = generator;
}

/**
 * Register an end date generator, to generate the end date given a product code
 * @param productCode {string} The code of a product
 * @param generator {function} A function that returns a date
 */
function registerEndDateGenerator(productCode, generator) {
    END_DATE_GENERATOR_BY_PRODUCT_CODE[productCode] = generator;
}

/**
 * A class to be overridden by external code in order to implement a feature parser for a vector layer
 */
class FeatureParser {

    /**
     * The name of a feature, given its properties
     * @param properties {Object} A map of prop name to prop value
     * @return {string|undefined}
     */
    getName(properties) {
    }

    /**
     * The WMS url for an additional layer to be shown on feature click
     * @param properties {Object} A map of prop name to prop value
     * @param timeParams {Object} A map of time parameter name to time parameter value
     * @return {string|undefined}
     */
    getWmsUrl(properties, timeParams) {
        return undefined;
    }

    /**
     * Get the formatted values and labels to show on a popup on feature click
     * @param properties {Object} A map of prop name to prop value
     * @param styler {ChartStyler} The styler for the layer of this feature
     * @param timeParams {Object} A map of time parameter name to time parameter value
     * @return {Object} A map from property label to property formatted value
     */
    getFormattedProperties(properties, styler, timeParams) {
    }
}

window.tumbo.FeatureParser = FeatureParser;
window.tumbo.registerFeatureParser = registerFeatureParser;
window.tumbo.registerUrlParamsGenerator = registerUrlParamsGenerator;
window.tumbo.registerEndDateGenerator = registerEndDateGenerator;
export {
    LayerService,
    Layer,
    BaseLayer,
    ContextLayer,
    LayerTree,
    ContextLayerList,
    BaseLayerList,
    FeatureParser,
    registerFeatureParser,
    registerUrlParamsGenerator,
    registerEndDateGenerator
}

